import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Space,
  Statistic,
  Layout,
  Button,
  Spin,
  message,
  Table,
  Tabs,
  Tooltip,
  Modal,
  Popover,
} from 'antd';

import styles from "./index.module.css";
import { fetchAllTransportOrganizations, fetchTCDrivers, resetDrivers } from '../../../store/transport_organizations';
import { fetchAllHealthOrganizations } from '../../../store/health_organizations';
import { getReportingRides } from '../../../store/rides';

import {
  DownOutlined,
  CarOutlined,
  StarFilled,
  StarOutlined
} from '@ant-design/icons';
import { CalendarIcon, OtherOrganizationIcon, RidesCompletedIcon, RideCancelledIcon, TotalMemberedServeIcon, UniqueMemberServedIcon, RiderNoShowIcon, FiltersIcon, OverviewIcon, AccessIcon, DetailedViewIcon, GeoInsightsIcon, CancellationIcon, ReportsIcon, InfoIcon, MemberSatisfactionIcon, TcPerformanceIcon, ReloadIcon, EnlargeIcon, SortIcon, SORT_CARET_DATA_URI } from '../../../assets';
import MedhaulStartIcon from '../../../assets/svgs/MedhaulStartIcon';
import { CodoAIIcon } from '../../../assets/svgs/CodiAIIcon';
import * as d3 from 'd3';

import * as XLSX from 'xlsx';

import dayjs from 'dayjs';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import classNames from 'classnames';
import { ReportsSection } from '../../../components';
import { GroupedCheckboxFilterMenu } from '../../../components/common/FilterPanel/FilterComponents/GroupedCheckboxFilterMenu/GroupedCheckboxFilterMenu';
import menuStyles from '../../../components/common/FilterPanel/FilterComponents/GroupedCheckboxFilterMenu/OrganizationDropdown.module.css';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { RIDE_STATUSES, USER_ROLES, VEHICLE_OPTIONS, VEHICLE_TYPES, VEHICLE_TYPES_LABEL, EXPORT_MESSAGES } from '../../../constants/constants';
import UberHealthIcon from '../../../assets/svgs/uberHealth.svg';
import {
  getDetailedViewFirstTwoWordsLines,
  getDetailedViewOrganizationLines,
  getDetailedViewStatusColor,
  getDetailedViewVehicleTypeDisplay,
  getDetailedViewVehicleTypeFilterLabel,
  getDetailedViewVehicleTypeFullLabel,
  getOrganizationName,
  getTransportCompanyName,
  isHoAdminInsightsView,
  statusLabel,
} from './detailedViewHelpers';
import { InsightsRideDetailsModal } from './InsightsRideDetailsModal';
import { exportReport } from '../../../store/reports';
import { useSelector } from 'react-redux';


dayjs.extend(customParseFormat);

// Custom D3 Charts
const RidesByCountyChart = React.lazy(() => import('../../../components/charts/RidesByCountyChart/RidesByCountyChart'));
const MonthlyTrendChart = React.lazy(() => import('../../../components/charts/MonthlyTrendChart/MonthlyTrendChart'));
const BarChart = React.lazy(() => import('../../../components/charts/barChart/barchart'));
const VehicleTypeChart = React.lazy(() => import('../../../components/charts/VehicleTypeChart/VehicleTypeChart'));
const CancellationStackedReasonChart = React.lazy(() => import('../../../components/charts/CancellationStackedReasonChart/CancellationStackedReasonChart'));
const CancellationSemiDonutChart = React.lazy(() => import('../../../components/charts/CancellationSemiDonutChart/CancellationSemiDonutChart'));

const { Header, Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;

const insightsFilterSelectBuiltinPlacements = {
  bottomLeft: {
    points: ['tc', 'bc'],
    offset: [0, 4],
    overflow: { adjustX: true, adjustY: true, shiftY: true },
    htmlRegion: 'visible',
  },
  bottomRight: {
    points: ['tr', 'br'],
    offset: [0, 4],
    overflow: { adjustX: true, adjustY: true, shiftY: true },
    htmlRegion: 'visible',
  },
  topLeft: {
    points: ['bl', 'tl'],
    offset: [0, -4],
    overflow: { adjustX: true, adjustY: true, shiftY: true },
    htmlRegion: 'visible',
  },
  topRight: {
    points: ['br', 'tr'],
    offset: [0, -4],
    overflow: { adjustX: true, adjustY: true, shiftY: true },
    htmlRegion: 'visible',
  },
};

/** Center calendar under range trigger */
const insightsDateRangeDropdownAlign = {
  points: ['tc', 'bc'],
  offset: [0, 4],
  overflow: { adjustX: true, adjustY: true },
};

const statusColors = {
  PENDING: '#faad14',
  ACCEPTED: '#2E84FD',
  START: '#13c2c2',
  IN_PROGRESS: '#7f8c8d',
  COMPLETED: '#2467D2',
  EXPIRED: '#9864E7',
  CANCELED: '#FB9C2B',
  UNFULFILLED: '#722ed1',
  UNABLE_TO_FULFILL: '#F97500',
  RESCHEDULED: '#b37feb'
};


const vehicleTypesColors = {
  ambulatoryAssistRiderIsWalking: '#39DE82',
  uberAmbulatoryRide: '#2E84FD',
  ambulatoryRiderIsWalking: '#FB9C2B',
  stretcherBoundBariatric: '#F97500',
  wheelchairBoundBariatric: '#405E8C',
  stretcherBoundNonBariatric: '#F57C00',
  wheelchairBoundNonBariatric: '#405E8C'
};

const MH_Reporting_Dashboard = ({

  fetchAllTransportOrganizations,
  getReportingRides,
  fetchTCDrivers,
  resetDrivers,
  fetchAllHealthOrganizations,
  transportCompanies,
  drivers,
  ridesData,
  summary,
  exportReport,
  loading,
  healthOrganizations }

) => {

  const [dateRange, setDateRange] = useState([dayjs().subtract(1, 'month'), dayjs()]);
  const [selectedOrg, setSelectedOrg] = useState([]);
  const [selectedOrgItems, setSelectedOrgItems] = useState([]);
  const [orgPopoverVisible, setOrgPopoverVisible] = useState(false);
  const [selectedTransport, setSelectedTransport] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedVehicleType, setSelectedVehicleType] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [activeTab, setActiveTab] = useState('1');
  const [selectedRide, setSelectedRide] = useState(null);
  const [rideModalVisible, setRideModalVisible] = useState(false);
  const [rideStatusModalVisible, setRideStatusModalVisible] = useState(false);
  const [utilizationModalVisible, setUtilizationModalVisible] = useState(false);
  const [vehicleTypeModalVisible, setVehicleTypeModalVisible] = useState(false);
  const [riderMetricsModalVisible, setRiderMetricsModalVisible] = useState(false);
  const [riskAnalysisModalVisible, setRiskAnalysisModalVisible] = useState(false);
  const [geoInsightsModalVisible, setGeoInsightsModalVisible] = useState(false);
  const [costCenterDistanceModalVisible, setCostCenterDistanceModalVisible] = useState(false);
  const [userActivityModalVisible, setUserActivityModalVisible] = useState(false);
  const [accountActivityModalVisible, setAccountActivityModalVisible] = useState(false);
  const [locationMapModalVisible, setLocationMapModalVisible] = useState(false);
  const [hoCancellationModalVisible, setHoCancellationModalVisible] = useState(false);
  const [riderCancellationModalVisible, setRiderCancellationModalVisible] = useState(false);
  const [driverCancellationModalVisible, setDriverCancellationModalVisible] = useState(false);
  const [tcCancellationModalVisible, setTcCancellationModalVisible] = useState(false);
  const [canceledVsRescheduledModalVisible, setCanceledVsRescheduledModalVisible] = useState(false);
  const [detailedViewFilters, setDetailedViewFilters] = useState({});
  const [detailedViewPageSize, setDetailedViewPageSize] = useState(10);

  const userInfo = useSelector((state) => state.authorization?.user);
  const userRole = useSelector((state) => state.authorization?.user?.role)
  const authorizationRole = useSelector((state) => state.authorization?.role);
  const viewDashboardMode = useSelector((state) => state.authorization?.viewDashboardMode);
  const hideTransportCompanyAndDriver = isHoAdminInsightsView({
    authorizationRole,
    viewDashboardMode,
    user: userInfo,
  });
  const hideTcDriverCancellations =
    hideTransportCompanyAndDriver ||
    [USER_ROLES.HO_ADMIN, USER_ROLES.HO_EMPLOYEE, USER_ROLES.ENTERPRISE_ADMIN].includes(
      authorizationRole || userRole
    );
  const viewComponents = useSelector((state) => state.authorization?.user?.organization ?
    state.authorization?.user?.organization?.addons?.insightsEnabled : state.authorization?.user?.orgAddons?.insightsEnabled);
  const disabledDate = () => false;

  const insightsFilterCompanyTitle = useMemo(() => {
    if (selectedTransport === 'all') return 'All Companies';
    return transportCompanies.find((t) => t.id === selectedTransport)?.name ?? '';
  }, [selectedTransport, transportCompanies]);

  const superAdminOrgSections = useMemo(() => {
    const enterprise = [];
    const standard = [];
    const list = healthOrganizations || [];

    const childrenByParentId = list.reduce((acc, org) => {
      if (!org?.parentOrgId) return acc;
      const child = { key: org.id, title: org.name, value: { id: org.id, name: org.name } };
      acc[org.parentOrgId] = [...(acc[org.parentOrgId] || []), child];
      return acc;
    }, {});

    list.forEach((h) => {
      if (!h || h.parentOrgId) return;
      const nestedChildren = h.children?.length
        ? h.children.map(c => ({ key: c.id, title: c.name, value: { id: c.id, name: c.name } }))
        : childrenByParentId[h.id] || [];
      const item = {
        key: h.id,
        title: h.name,
        value: { id: h.id, name: h.name },
        ...(nestedChildren.length && { children: nestedChildren }),
      };
      (h.isEnterprise === true || nestedChildren.length > 0 ? enterprise : standard).push(item);
    });

    return [
      { id: 'enterprise', title: 'Enterprise', items: enterprise, showTrailingArrow: true },
      { id: 'standard', title: 'Standard', items: standard, showTrailingArrow: false },
    ];
  }, [healthOrganizations]);

  const enterpriseChildOrgSections = useMemo(() => {
    const items = (healthOrganizations || []).map(c => ({ key: c.id, title: c.name, value: { id: c.id, name: c.name } }));
    return [{ id: 'locations', title: 'Locations', items, showTrailingArrow: false }];
  }, [healthOrganizations]);

  const insightsDateRangeTitle = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return '';
    return `${dateRange[0].format('MMM D, YYYY')} - ${dateRange[1].format('MMM D, YYYY')}`;
  }, [dateRange]);

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      color += ('00' + ((hash >> (i * 8)) & 0xFF).toString(16)).slice(-2);
    }
    return color;
  }

  const rideDistanceCategories = [
    { key: 'urban', label: 'Urban (≤ 50 miles)', color: '#9864E7' },
    { key: 'subUrban', label: 'Suburban (51-75 miles)', color: '#FB9C2B' },
    { key: 'rural', label: 'Rural (> 75 miles)', color: '#D9DAE0' },
  ];

  const rideReschduleCancellation = [
    { key: 'RESCHEDULED', label: 'Rescheduled Rides', color: '#45D483' },
    { key: 'CANCELED', label: 'Canceled', color: '#2E84FD' },
    { key: 'RESCHEDULED_CANCELED', label: 'Reschedule & Canceled', color: '#3E5A87' },
  ];

  const TcStatus = [
    { key: 'COMPLETED', label: 'Completed', color: '#5461AA' },
    { key: 'OTHER', label: 'Other', color: '#05B8D9' },

  ];

  const rideStatuses = [
    { key: 'rides', label: 'All Rides', color: '#39AD00' },
    { key: 'PENDING', label: 'Pending', color: '#DC7777' },
    { key: 'ACCEPTED', label: 'Accepted', color: statusColors.ACCEPTED },
    { key: 'IN_PROGRESS', label: 'In Progress', color: '#979797' },
    { key: 'CANCELED', label: 'Canceled', color: statusColors.CANCELED },
    { key: 'EXPIRED', label: 'Expired', color: '#D9D9D9' },
    { key: 'UNFULFILLED', label: 'Unfulfilled', color: statusColors.UNFULFILLED },
    { key: 'UNABLE_TO_FULFILL', label: 'Unable to Fulfill', color: '#2E84FD' },
    { key: 'COMPLETED', label: 'Completed', color: '#FB9C2B' },
    { key: 'RESCHEDULED', label: 'Rescheduled', color: '#BB8EFF' },
  ];
  // Star rating component
  const renderStarRating = (score, size = 20) => {
    const fullStars = Math.floor(score);
    const hasHalfStar = score % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className={styles.starRow}>
        {[...Array(fullStars)].map((_, i) => (
          <StarFilled key={`full-${i}`} style={{ color: '#ffc53d', fontSize: size }} />
        ))}
        {hasHalfStar && (
          <StarFilled key="half" style={{ color: '#ffc53d', fontSize: size, opacity: 0.5 }} />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <StarOutlined key={`empty-${i}`} style={{ color: '#D9D9D9', fontSize: size }} />
        ))}
      </div>
    );
  };


  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (viewComponents && userRole === USER_ROLES.HO_ADMIN) {
      if (userInfo.permissions?.orgList) {
        fetchAllHealthOrganizations();
      } else {
        setSelectedOrg(userInfo?.organizationId ? [userInfo.organizationId] : []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewComponents, userRole, userInfo?.organizationId, fetchAllHealthOrganizations]);

  useEffect(() => {
    if (userRole === USER_ROLES.SUPER_ADMIN) {
      fetchAllTransportOrganizations();
      fetchAllHealthOrganizations();
    }

    if (userRole === USER_ROLES.ENTERPRISE_ADMIN) {
      fetchAllHealthOrganizations();
    }

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAllTransportOrganizations, fetchAllHealthOrganizations]);


  useEffect(() => {
    if (selectedTransport && selectedTransport !== 'all') {
      // Call fetchTCDrivers when a valid transport company is selected
      fetchTCDrivers(selectedTransport);
    } else {
      // Reset drivers if no transport company is selected
      resetDrivers();
    }

    // Reset the selected driver to 'all' whenever the transport company changes
    setSelectedDriver('all');
  }, [selectedTransport, fetchTCDrivers, resetDrivers]);

  // Fetch reference data (orgs, transport companies, drivers)
  const fetchReferenceData = useCallback(async () => {
    try {
      // In a real app, these would be API calls


      // const mockDrivers = [
      //   { id: 'driver1', name: 'John Smith' },
      //   { id: 'driver2', name: 'Maria Garcia' },
      //   { id: 'driver3', name: 'David Johnson' }
      // ];


      // setDrivers(mockDrivers);
    } catch (error) {
      message.error('Failed to load reference data');
      console.error('Error loading reference data:', error);
    }
  }, []);

  // Fetch rides data from API
  const fetchData = useCallback(async () => {

    try {
      if (!dateRange || dateRange.length !== 2) {
        return;
      }

      await getReportingRides({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        healthOrganizationId: (() => {
          if (userRole === USER_ROLES.SUPER_ADMIN) {
            return selectedOrgItems.length > 0 ? selectedOrgItems.map((o) => o.id) : undefined;
          }
          if (userRole === USER_ROLES.ENTERPRISE_ADMIN) {
            return selectedOrgItems.length > 0 ? selectedOrgItems.map((o) => o.id) : undefined;
          }
          if (userRole === USER_ROLES.HO_ADMIN) {
            if (healthOrganizations.length > 1 && selectedOrgItems.length > 0) {
              return selectedOrgItems.map((o) => o.id);
            }
            return userInfo?.organizationId ? [userInfo.organizationId] : undefined;
          }
          return selectedOrg.length > 0 ? selectedOrg : undefined;
        })(),
        transportCompanyId: selectedTransport !== 'all' ? selectedTransport : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        vehicleType: selectedVehicleType !== 'all' ? selectedVehicleType : undefined,
        driverId: selectedDriver !== 'all' ? selectedDriver : undefined
      });


    } catch (error) {
      message.error('Failed to fetch ride data');
      console.error('Error fetching ride data:', error);
    } finally {

    }
  }, [dateRange, selectedOrg, selectedOrgItems, selectedTransport, selectedStatus, selectedVehicleType, selectedDriver, userRole, userInfo?.organizationId, healthOrganizations.length, getReportingRides]);

  const handleRefreshFilters = useCallback(() => {
    const nextDateRange = [dayjs().subtract(1, 'month'), dayjs()];
    const isMultiOrgHo =
      viewComponents &&
      userRole === USER_ROLES.HO_ADMIN &&
      healthOrganizations.length > 1;
    const nextOrg =
      viewComponents && userRole === USER_ROLES.HO_ADMIN && !isMultiOrgHo
        ? (userInfo?.organizationId ? [userInfo.organizationId] : [])
        : [];

    setDateRange(nextDateRange);
    setSelectedOrg(nextOrg);
    setSelectedOrgItems([]);
    setOrgPopoverVisible(false);
    setSelectedTransport('all');
    setSelectedStatus('all');
    setSelectedVehicleType('all');
    setSelectedDriver('all');
  }, [viewComponents, userRole, userInfo?.organizationId, healthOrganizations.length]);

  // Initial data load
  useEffect(() => {
    fetchReferenceData();
    fetchData();
  }, [fetchReferenceData, fetchData]);

  // Processed data for charts
  const processedData = useMemo(() => {
    if (!ridesData || ridesData?.length === 0) {
      return {
        statusCounts: {},
        monthlyTrends: [],
        orgPerformance: [],
        transportPerformance: [],
        driverPerformance: [],
        vehicleTypeCounts: {},
        counties: [],
        hoActivityCounts: {},
        cancellationData: {},
        monthlyPatientTrends: [],
        recurrenceAnalysis: {},
        timeMetrics: {
          avgResponseTime: 'N/A',
          avgLoadTime: 'N/A',
          avgRideDuration: 'N/A',
          avgWaitTime: 'N/A',
          avgMileage: 'N/A'
        },
        rawData: []
      };
    }


    // Status distribution
    const statusCounts = ridesData.reduce((acc, ride) => {
      if (
        hideTcDriverCancellations &&
        ride.status === RIDE_STATUSES.CANCELED &&
        isTcOrDriverCancellation(ride.cancellationInfo)
      ) {
        return acc;
      }
      acc[ride.status] = (acc[ride.status] || 0) + 1;

      return acc;
    }, {});


    // Vehicle type distribution
    const vehicleTypeCounts = ridesData.reduce((acc, ride) => {
      const type = ride.vehicleType || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    // Cost Center distribution
    const costCenterCounts = ridesData.reduce((acc, ride) => {
      let costCenter = ride.costCenter ? ride.costCenter.trim() : '';

      // If cost center is empty or not assigned, use organization name
      if (!costCenter) {
        const org = healthOrganizations.find(o => o.id === ride.healthOrganizationId);
        costCenter = org ? org.name : 'Not Assigned';
      }

      acc[costCenter] = (acc[costCenter] || 0) + 1;
      return acc;
    }, {});

    // Extract county from pickup address (assuming format like "123 Main St, King County")
    const countyData = ridesData.reduce((acc, ride) => {
      const countyMatch = ride.pickupAddressDetails.county;
      const county = countyMatch ? countyMatch.trim() : 'Unknown';
      acc[county] = (acc[county] || 0) + 1;
      return acc;
    }, {});

    const counties = Object.entries(countyData)
      .map(([county, count]) => ({ county, count }))
      .sort((a, b) => b.count - a.count);


    const hoActivityCounts = getHOActivityCount(ridesData, 'createdBy');



    // Monthly trends
    const monthlyTrends = ridesData.reduce((acc, ride) => {
      const month = dayjs(ride.pickUpDate).format('MMM YYYY');
      if (!acc[month]) {
        acc[month] = {
          month,
          rides: 0,
          PENDING: 0,
          ACCEPTED: 0,
          IN_PROGRESS: 0,
          COMPLETED: 0,
          CANCELED: 0,
          EXPIRED: 0,
          UNFULFILLED: 0,
          UNABLE_TO_FULFILL: 0,
          RESCHEDULED: 0,
          RESCHEDULED_CANCELED: 0,

        };
      }
      acc[month].rides++;

      if (ride.isRescheduled === true) acc[month].RESCHEDULED++;
      if (ride.isRescheduled === true && ride.status === RIDE_STATUSES.CANCELED) {
        if (!(hideTcDriverCancellations && isTcOrDriverCancellation(ride.cancellationInfo))) {
          acc[month].RESCHEDULED_CANCELED++;
        }
      }
      if (ride.status === RIDE_STATUSES.CANCELED) {
        if (!(hideTcDriverCancellations && isTcOrDriverCancellation(ride.cancellationInfo))) {
          acc[month].CANCELED++;
        }
      }
      if (ride.status === RIDE_STATUSES.COMPLETED) acc[month].COMPLETED++;
      if (ride.status === RIDE_STATUSES.PENDING) acc[month].PENDING++;
      if (ride.status === RIDE_STATUSES.ACCEPTED) acc[month].ACCEPTED++;
      if (ride.status === RIDE_STATUSES.IN_PROGRESS) acc[month].IN_PROGRESS++;
      if (ride.status === RIDE_STATUSES.EXPIRED) acc[month].EXPIRED++;
      if (ride.status === RIDE_STATUSES.UNFULFILLED) acc[month].UNFULFILLED++;
      if (ride.status === RIDE_STATUSES.UNABLE_TO_FULFILL) acc[month].UNABLE_TO_FULFILL++;


      return acc;
    }, {});



    // Organization performance
    const orgPerformance = ridesData.reduce((acc, ride) => {
      const orgId = ride.healthOrganizationId;
      const orgName = getOrganizationName(ride, healthOrganizations);

      if (!acc[orgId]) {
        acc[orgId] = {
          org: orgName,
          COMPLETED: 0,
          FAILED: 0,
          PENDING: 0,
          EXPIRED: 0,
          CANCELED: 0,
          UNABLE_TO_FULFILL: 0
        };
      }
      if (ride.status === RIDE_STATUSES.COMPLETED) acc[orgId].COMPLETED++;
      if (ride.status === RIDE_STATUSES.CANCELED) {
        if (!(hideTcDriverCancellations && isTcOrDriverCancellation(ride.cancellationInfo))) {
          acc[orgId].CANCELED++;
        }
      }
      if (ride.status === RIDE_STATUSES.EXPIRED) acc[orgId].EXPIRED++;
      if (ride.status === RIDE_STATUSES.UNFULFILLED) acc[orgId].UNABLE_TO_FULFILL++;
      if (ride.status === RIDE_STATUSES.PENDING) acc[orgId].PENDING++;
      return acc;
    }, {});

    // Transport company performance
    const transportPerformance = ridesData.reduce((acc, ride) => {
      const tcId = ride.transportCompanyId;
      const tcName = getTransportCompanyName(ride, transportCompanies);

      if (!acc[tcId]) {
        acc[tcId] = {
          company: tcName,
          COMPLETED: 0,
          OTHER: 0,
          PENDING: 0,
          avgResponseTime: 0,
          responseTimeCount: 0
        };
      }
      if (ride.status === RIDE_STATUSES.COMPLETED) acc[tcId].COMPLETED++;
      if ([RIDE_STATUSES.EXPIRED, RIDE_STATUSES.UNABLE_TO_FULFILL].includes(ride.status)) acc[tcId].OTHER++;
      if (ride.status === RIDE_STATUSES.PENDING) acc[tcId].PENDING++;

      // Calculate response time metrics
      if (ride.acceptanceTime && ride.createdAt) {
        const responseMs = new Date(ride.acceptanceTime) - new Date(ride.createdAt);
        acc[tcId].avgResponseTime += responseMs;
        acc[tcId].responseTimeCount++;
      }
      return acc;
    }, {});

    // Calculate average response times
    Object.values(transportPerformance).forEach(tc => {
      if (tc.responseTimeCount > 0) {
        tc.avgResponseTime = formatDuration(tc.avgResponseTime / tc.responseTimeCount);
      } else {
        tc.avgResponseTime = 'N/A';
      }
    });

    // Driver performance
    const driverPerformance = ridesData.reduce((acc, ride) => {
      if (!ride.driverId) return acc;

      const driverId = ride.driverId;
      // console.log('Driver ID:', driverId);
      // console.log('Driver Name:', drivers);
      // const driverName = drivers.find(d => d.id === driverId)?.name || `Driver ${driverId}`;

      if (!acc[driverId]) {
        acc[driverId] = {
          driver: ride.driverName,
          COMPLETED: 0,
          FAILED: 0,
          avgRating: 0,
          ratingCount: 0,
          totalMiles: 0
        };
      }
      if (ride.status === RIDE_STATUSES.COMPLETED) acc[driverId].COMPLETED++;
      if ([RIDE_STATUSES.EXPIRED, RIDE_STATUSES.UNABLE_TO_FULFILL].includes(ride.status)) acc[driverId].failed++;

      // Calculate driver metrics (assuming we have rating and mileage data)
      if (ride.driverRating) {
        acc[driverId].avgRating += ride.driverRating;
        acc[driverId].ratingCount++;
      }
      if (ride.actMileage) {
        acc[driverId].totalMiles += parseFloat(ride.actMileage) || 0;
      }
      return acc;
    }, {});

    // Calculate average ratings
    Object.values(driverPerformance).forEach(driver => {
      if (driver.ratingCount > 0) {
        driver.avgRating = (driver.avgRating / driver.ratingCount).toFixed(1);
      } else {
        driver.avgRating = 'N/A';
      }
    });



    // Recurrence analysis
    const recurrenceAnalysis = ridesData.reduce((acc, ride) => {
      const key = ride.isRecurring ? 'Recurring' : 'One-time';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    // Time metrics
    const timeMetrics = {
      avgResponseTime: calculateAverageTime(ridesData, 'acceptanceTime', 'createdAt'),
      avgLoadTime: calculateAverageTime(ridesData, 'loadTime', 'acceptanceTime'),
      avgRideDuration: calculateAverageTime(ridesData, 'unloadTime', 'loadTime'),
      avgWaitTime: calculateAverageWaitTime(ridesData),
      avgMileage: calculateAverageMileage(ridesData)
    };

    const monthlyTrendsData = Object.values(monthlyTrends).sort((a, b) =>
      dayjs(a.month, 'MMM YYYY').diff(dayjs(b.month, 'MMM YYYY')))




    // Calculate no-shows (assuming status indicates no-shows)
    const noShowsCount = summary?.noShowCancellations?.count;
    const noShowRatio = summary?.noShowCancellations?.ratio || 0;
    // Calculate satisfaction (mock data - replace with actual)
    const satisfactionScore = summary?.satisfactionScore || 0; // Average from your data
    const satisfactionResponses = summary?.satisfactionResponses || 0; // Total ratings from your data

    // Calculate unique patient count directly from rides data
    const uniquePatientIds = new Set(ridesData.map(ride => ride.riderInfoId).filter(id => id));
    const uniquePatientCount = uniquePatientIds.size;

    // Keep uniqueMembers from summary for other uses
    const uniqueMembers = {
      totalMembers: summary?.memberServedCount,
      uniqueCount: summary?.uniquePatientCount,
    };

    // Calculate monthly trends for different patient types
    const monthlyPatientTrends = getMonthlyPatientTrends(ridesData);
    const monthlyPatientTrendsData = Object.values(monthlyPatientTrends).sort((a, b) =>
      dayjs(a.month, 'MMM YYYY').diff(dayjs(b.month, 'MMM YYYY')))





    const cancellationData = processCancellationData(ridesData, hideTcDriverCancellations);

    // Cost Center by Distance Distribution (Urban/Suburban/Rural)
    const costCenterByDistance = ridesData.reduce((acc, ride) => {
      let costCenter = ride.costCenter ? ride.costCenter.trim() : '';

      // If cost center is empty or not assigned, use organization name
      if (!costCenter) {
        const org = healthOrganizations.find(o => o.id === ride.healthOrganizationId);
        costCenter = org ? org.name : 'Not Assigned';
      }

      if (!acc[costCenter]) {
        acc[costCenter] = {
          costCenter,
          urban: 0,
          subUrban: 0,
          rural: 0,
          total: 0
        };
      }

      const mileage = parseFloat(ride.actMileage || ride.estMileage || 0);
      if (mileage <= 50) {
        acc[costCenter].urban++;
      } else if (mileage <= 75) {
        acc[costCenter].subUrban++;
      } else {
        acc[costCenter].rural++;
      }
      acc[costCenter].total++;

      return acc;
    }, {});

    // Cost Center by County Matrix
    const costCenterByCounty = ridesData.reduce((acc, ride) => {
      let costCenter = ride.costCenter ? ride.costCenter.trim() : '';

      // If cost center is empty or not assigned, use organization name
      if (!costCenter) {
        const org = healthOrganizations.find(o => o.id === ride.healthOrganizationId);
        costCenter = org ? org.name : 'Not Assigned';
      }

      const county = ride.pickupAddressDetails?.county?.trim() || 'Unknown';

      const key = `${costCenter}|||${county}`;
      acc[key] = (acc[key] || 0) + 1;

      return acc;
    }, {});

    // Transform to array format for easier charting
    const costCenterCountyMatrix = Object.entries(costCenterByCounty).map(([key, count]) => {
      const [costCenter, county] = key.split('|||');
      return { costCenter, county, count };
    });

    // Cost Center Service Coverage Map - Pickup/Dropoff locations by cost center
    const costCenterLocations = ridesData.reduce((acc, ride) => {
      let costCenter = ride.costCenter ? ride.costCenter.trim() : '';

      // If cost center is empty or not assigned, use organization name
      if (!costCenter) {
        const org = healthOrganizations.find(o => o.id === ride.healthOrganizationId);
        costCenter = org ? org.name : 'Not Assigned';
      }

      if (!acc[costCenter]) {
        acc[costCenter] = {
          costCenter,
          pickupLocations: [],
          dropoffLocations: [],
          rides: []
        };
      }

      // Add pickup location if coordinates exist (check both direct and nested coordinates)
      const pickupLat = ride.pickupAddressDetails?.coordinates?.lat || ride.pickupAddressDetails?.lat;
      const pickupLng = ride.pickupAddressDetails?.coordinates?.lng || ride.pickupAddressDetails?.lng;

      if (pickupLat && pickupLng) {
        acc[costCenter].pickupLocations.push({
          lat: pickupLat,
          lng: pickupLng,
          address: ride.pickupAddressDetails.formattedAddress,
          county: ride.pickupAddressDetails.county,
          rideId: ride.seqId
        });
      }

      // Add dropoff location if coordinates exist (check both direct and nested coordinates)
      const dropoffLat = ride.destinationAddressDetails?.coordinates?.lat ||
        ride.destinationAddressDetails?.lat ||
        ride.destinationAddress?.coordinates?.lat ||
        ride.destinationAddress?.lat;
      const dropoffLng = ride.destinationAddressDetails?.coordinates?.lng ||
        ride.destinationAddressDetails?.lng ||
        ride.destinationAddress?.coordinates?.lng ||
        ride.destinationAddress?.lng;

      if (dropoffLat && dropoffLng) {
        acc[costCenter].dropoffLocations.push({
          lat: dropoffLat,
          lng: dropoffLng,
          address: ride.destinationAddressDetails?.formattedAddress || ride.destinationAddress?.formattedAddress,
          county: ride.destinationAddressDetails?.county || ride.destinationAddress?.county,
          rideId: ride.seqId
        });
      }

      acc[costCenter].rides.push(ride);

      return acc;
    }, {});

    // Top Routes by Cost Center - Pickup County → Dropoff County
    const costCenterRoutes = ridesData.reduce((acc, ride) => {
      let costCenter = ride.costCenter ? ride.costCenter.trim() : '';

      // If cost center is empty or not assigned, use organization name
      if (!costCenter) {
        const org = healthOrganizations.find(o => o.id === ride.healthOrganizationId);
        costCenter = org ? org.name : 'Not Assigned';
      }

      const pickupCounty = ride.pickupAddressDetails?.county?.trim() || 'Unknown';
      const dropoffCounty = ride.destinationAddressDetails?.county?.trim() || 'Unknown';

      const routeKey = `${pickupCounty} → ${dropoffCounty}`;
      const key = `${costCenter}|||${routeKey}`;

      if (!acc[key]) {
        acc[key] = {
          costCenter,
          pickupCounty,
          dropoffCounty,
          route: routeKey,
          count: 0
        };
      }
      acc[key].count++;

      return acc;
    }, {});

    // Group routes by cost center and get top routes
    const costCenterTopRoutes = {};
    Object.values(costCenterRoutes).forEach(route => {
      if (!costCenterTopRoutes[route.costCenter]) {
        costCenterTopRoutes[route.costCenter] = [];
      }
      costCenterTopRoutes[route.costCenter].push(route);
    });

    // Sort routes by count and get top 10 for each cost center
    Object.keys(costCenterTopRoutes).forEach(costCenter => {
      costCenterTopRoutes[costCenter].sort((a, b) => b.count - a.count);
      costCenterTopRoutes[costCenter] = costCenterTopRoutes[costCenter].slice(0, 10);
    });

    // Cost Center Activity Timeline - Rides by hour of day
    const costCenterActivityTimeline = ridesData.reduce((acc, ride) => {
      let costCenter = ride.costCenter ? ride.costCenter.trim() : '';

      // If cost center is empty or not assigned, use organization name
      if (!costCenter) {
        const org = healthOrganizations.find(o => o.id === ride.healthOrganizationId);
        costCenter = org ? org.name : 'Not Assigned';
      }

      const hour = dayjs(ride.pickUpDate).hour();

      if (!acc[costCenter]) {
        acc[costCenter] = {
          costCenter,
          hourly: Array(24).fill(0).map((_, i) => ({ hour: i, count: 0 })),
          total: 0
        };
      }

      acc[costCenter].hourly[hour].count++;
      acc[costCenter].total++;

      return acc;
    }, {});

    // Cost Center Cancellation and Completion by Location (County)
    const costCenterLocationPerformance = ridesData.reduce((acc, ride) => {
      let costCenter = ride.costCenter ? ride.costCenter.trim() : '';

      // If cost center is empty or not assigned, use organization name
      if (!costCenter) {
        const org = healthOrganizations.find(o => o.id === ride.healthOrganizationId);
        costCenter = org ? org.name : 'Not Assigned';
      }

      const county = ride.pickupAddressDetails?.county?.trim() || 'Unknown';
      const key = `${costCenter}|||${county}`;

      if (!acc[key]) {
        acc[key] = {
          costCenter,
          county,
          completed: 0,
          canceled: 0,
          expired: 0,
          total: 0,
          completionRate: 0,
          cancellationRate: 0
        };
      }

      acc[key].total++;

      if (ride.status === RIDE_STATUSES.COMPLETED) {
        acc[key].completed++;
      } else if (ride.status === RIDE_STATUSES.CANCELED) {
        acc[key].canceled++;
      } else if (ride.status === RIDE_STATUSES.EXPIRED) {
        acc[key].expired++;
      }

      return acc;
    }, {});

    // Calculate rates and transform to array
    const costCenterLocationPerformanceArray = Object.values(costCenterLocationPerformance).map(item => {
      item.completionRate = item.total > 0 ? ((item.completed / item.total) * 100).toFixed(1) : 0;
      item.cancellationRate = item.total > 0 ? (((item.canceled + item.expired) / item.total) * 100).toFixed(1) : 0;
      return item;
    });

    // Group by cost center for easier rendering
    const costCenterLocationPerformanceGrouped = {};
    costCenterLocationPerformanceArray.forEach(item => {
      if (!costCenterLocationPerformanceGrouped[item.costCenter]) {
        costCenterLocationPerformanceGrouped[item.costCenter] = [];
      }
      costCenterLocationPerformanceGrouped[item.costCenter].push(item);
    });

    // Sort each cost center's data by total rides
    Object.keys(costCenterLocationPerformanceGrouped).forEach(costCenter => {
      costCenterLocationPerformanceGrouped[costCenter].sort((a, b) => b.total - a.total);
    });

    return {
      statusCounts,
      monthlyTrends: monthlyTrendsData,
      orgPerformance: Object.values(orgPerformance),
      transportPerformance: Object.values(transportPerformance),
      driverPerformance: Object.values(driverPerformance),
      counties: counties,
      vehicleTypeCounts,
      costCenterCounts,
      recurrenceAnalysis,
      hoActivityCounts,
      cancellationData,
      noShowsCount,
      noShowRatio,
      uniqueMembers,
      uniquePatientCount,
      satisfactionScore,
      satisfactionResponses,
      rawData: ridesData,
      timeMetrics: timeMetrics,
      monthlyPatientTrends: monthlyPatientTrendsData,
      costCenterByDistance: Object.values(costCenterByDistance),
      costCenterCountyMatrix,
      costCenterLocations: Object.values(costCenterLocations),
      costCenterTopRoutes,
      costCenterActivityTimeline: Object.values(costCenterActivityTimeline),
      costCenterLocationPerformance: costCenterLocationPerformanceGrouped

    };
    // Helpers used below are function declarations in this file; keep memo scoped to data inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ridesData, healthOrganizations, transportCompanies, drivers, hideTcDriverCancellations]);




  function getHOActivityCount(ridesData, property = 'createdBy') {
    const result = {};
    ridesData.forEach(ride => {
      const user = ride[property];
      if (user && user.firstName && user.lastName) {
        const key = `${user.firstName} ${user.lastName}`;
        result[key] = (result[key] || 0) + 1;
      }
    });
    return result;
  }


  function getMonthlyPatientTrends(ridesData) {
    return ridesData.reduce((acc, ride) => {
      const month = dayjs(ride.pickUpDate).format('MMM YYYY');
      if (!acc[month]) {
        acc[month] = {
          month,
          allPatients: 0,
          specialAccommodations: 0,
          riderNoShow: 0,
          frequentScheduled: 0,
          urban: 0,
          subUrban: 0,
          rural: 0
        };
      }

      acc[month].allPatients++;

      if (ride.riderInfo?.specialAccommodations) {
        acc[month].specialAccommodations++;
      }

      // if (ride.riderInfo?.specialAccommodations) {
      //   acc[month].specialAccommodations++;
      // }


      if (ride.riderInfo?.frequentFlyer) {
        acc[month].frequentScheduled++;
      }

      // Determine frequently scheduled (example: 3+ rides in same month)
      // const riderMonthlyRides = ridesData.filter(r =>
      //   r.riderInfoId === ride.riderInfoId &&
      //   dayjs(r.pickUpDate).format('MMM YYYY') === month
      // ).length;

      // if (riderMonthlyRides >= 3) {
      //   acc[month].frequentScheduled++;
      // }

      const riderNoShow = getRiderNoShowCancellationsByMonth(ridesData, month);
      const rideTypeCounts = getUrbanSuburbanRuralCountsByMonth(ridesData, month);

      acc[month].urban = rideTypeCounts?.urban || 0;
      acc[month].subUrban = rideTypeCounts?.subUrban || 0;
      acc[month].rural = rideTypeCounts?.rural || 0;
      acc[month].riderNoShow = riderNoShow.length;



      return acc;
    }, {});
  }



  function sanitizeCancellationReason(reason) {
    if (reason == null || reason === '') return 'Not specified';
    const cleaned = String(reason)
      .replace(/\s*\(undefined\)/gi, '')
      .replace(/\bundefined\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return cleaned || 'Not specified';
  }

  function resolveCancellationChartCategory(cancellationInfo) {
    const canceledBy = cancellationInfo?.canceledBy;
    const cancellationType = cancellationInfo?.cancellationType;
    const organizationType = cancellationInfo?.organizationType;
    const reason = sanitizeCancellationReason(cancellationInfo?.cancellationReason).toLowerCase();

    // Driver assignment/arrival history is not a cancellation reason
    if (reason.includes('uber driver assigned') || reason.includes('driver arrived')) {
      return null;
    }

    if (canceledBy === 'HO_ADMIN' || canceledBy === 'HO_EMPLOYEE' || canceledBy === 'ENTERPRISE_ADMIN') {
      return 'HO_ADMIN';
    }
    if (canceledBy === 'TC_ADMIN' || canceledBy === 'TC_EMPLOYEE') {
      return 'TC_ADMIN';
    }
    if (canceledBy === 'DRIVER') {
      return 'DRIVER';
    }
    if (canceledBy === 'RIDER') {
      return 'RIDER';
    }
    if (canceledBy === 'SUPER_ADMIN' || canceledBy === 'MH_ADMIN') {
      return 'MH_ADMIN';
    }

    // Older records often have null cancelBy — use cancellationType
    if (cancellationType === 'cancelByHO' || cancellationType === 'cancelByMHAdmin') {
      return 'HO_ADMIN';
    }
    if (cancellationType === 'cancelByTC' || cancellationType === 'riderNoShow') {
      return 'TC_ADMIN';
    }
    if (cancellationType === 'cancelByDriver') {
      return 'DRIVER';
    }
    if (cancellationType === 'cancelByRider') {
      return 'RIDER';
    }

    // Uber / system cancels often have null cancelBy + null cancellationType
    if (reason.includes('uber')) {
      if (reason.includes('rider')) return 'RIDER';
      if (reason.includes('driver')) return 'DRIVER';
      return 'TC_ADMIN';
    }

    if (organizationType === 'Transport') {
      return 'TC_ADMIN';
    }
    if (organizationType === 'Health') {
      if (reason.startsWith('i have ')) return 'RIDER';
      return 'HO_ADMIN';
    }

    return canceledBy || null;
  }

  function isTcOrDriverCancellation(cancellationInfo) {
    if (!cancellationInfo) return false;
    const category = resolveCancellationChartCategory(cancellationInfo);
    return category === 'TC_ADMIN' || category === 'DRIVER';
  }

  function processCancellationData(ridesData, hideTcDriverCancels = false) {
    const canceledRides = ridesData.filter(ride =>
      ride.status === 'CANCELED' &&
      !(hideTcDriverCancels && isTcOrDriverCancellation(ride.cancellationInfo))
    );

    const chartCategory = (ride) =>
      resolveCancellationChartCategory(ride.cancellationInfo) ||
      (ride.cancellationInfo ? null : 'HO_ADMIN');

    // Group by chart category and then by cancellationReason
    const cancellationData = d3.rollups(
      canceledRides.filter((ride) => chartCategory(ride)),
      v => d3.rollups(
        v,
        vs => vs.length,
        d => sanitizeCancellationReason(d.cancellationInfo?.cancellationReason)
      ),
      d => chartCategory(d)
    );

    // Convert to format needed for donut charts
    const result = {};
    cancellationData.forEach(([canceledBy, reasons]) => {
      result[canceledBy] = reasons.map(([reason, count]) => ({
        reason,
        count
      }));
    });

    // Ensure all categories exist (even if empty)
    const allCategories = ['HO_ADMIN', 'TC_ADMIN', 'DRIVER', 'RIDER', 'MH_ADMIN'];
    allCategories.forEach(cat => {
      if (!result[cat]) {
        result[cat] = [];
      }
    });

    return result;
  };

  function getUrbanSuburbanRuralCountsByMonth(ridesData, month) {
    const result = {
      urban: 0,
      subUrban: 0,
      rural: 0,
    };

    ridesData.forEach(ride => {
      if (
        ride.status === 'COMPLETED' &&
        dayjs(ride.pickUpDate).format('MMM YYYY') === month
      ) {
        if (ride.actMileage <= 50) {
          result.urban += 1;
        } else if (ride.actMileage <= 75) {
          result.subUrban += 1;
        } else if (ride.actMileage > 75) {
          result.rural += 1;
        }
      }
    });

    return result;
  }

  function getRiderNoShowCancellationsByMonth(ridesData, month) {
    return ridesData.filter(
      r =>
        r?.status === 'CANCELED' &&
        r?.cancellationInfo?.cancellationType === 'riderNoShow' &&
        dayjs(r.pickUpDate).format('MMM YYYY') === month
    );
  }




  // Helper function to calculate average time between two timestamps
  function calculateAverageTime(rides, endField, startField) {
    const validRides = rides.filter(ride => ride[endField] && ride[startField]);
    if (validRides.length === 0) return 'N/A';

    const totalMs = validRides.reduce((sum, ride) => {
      return sum + (new Date(ride[endField]) - new Date(ride[startField]));
    }, 0);

    const avgMs = totalMs / validRides.length;
    return formatDuration(avgMs);
  }

  // Helper function to calculate average wait time
  function calculateAverageWaitTime(rides) {
    const validRides = rides.filter(ride => ride.waitTime);
    if (validRides.length === 0) return 'N/A';

    const totalMinutes = validRides.reduce((sum, ride) => sum + parseInt(ride.waitTime || 0), 0);
    const avgMinutes = totalMinutes / validRides.length;
    return `${Math.round(avgMinutes)} mins`;
  }

  // Helper function to calculate average mileage
  function calculateAverageMileage(rides) {
    const validRides = rides.filter(ride => ride.actMileage);
    if (validRides.length === 0) return 'N/A';

    const totalMiles = validRides.reduce((sum, ride) => sum + parseFloat(ride.actMileage || 0), 0);
    const avgMiles = totalMiles / validRides.length;
    return `${avgMiles.toFixed(1)} miles`;
  }

  // Format duration in milliseconds to human-readable format
  function formatDuration(ms) {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours} hrs ${minutes % 60} mins`;
    if (minutes > 0) return `${minutes} mins`;
    return `${seconds} secs`;
  }




  const exportDetailedViewToExcel = () => {
    if (!ridesData || ridesData.length === 0) {
      message.warning('No data to export');
      return;
    }

    try {
      // Format the data for export with readable column names
      const formattedData = ridesData.map(ride => ({
        'Ride ID': ride.seqId,
        'Status': ride.status,
        'Pickup Date': dayjs(ride.pickUpDate).format('MMM D, YYYY h:mm A'),
        'Vehicle Type': ride.vehicleType || 'N/A',
        'Organization': getOrganizationName(ride, healthOrganizations),
        'Transport Company': getTransportCompanyName(ride, transportCompanies),
        'Driver': ride.driverName || 'Unassigned',
        'Pickup Address': ride.pickupAddressDetails?.formattedAddress || 'N/A',
        'Destination': ride.destinationAddressDetails?.formattedAddress || 'N/A',
        'Cost Center': ride.costCenter || 'Not Assigned',
        'Created At': dayjs(ride.createdAt).format('MMM D, YYYY h:mm A'),
        'Is Recurring': ride.isRecurring ? 'Yes' : 'No',
        'Wait Time': ride.waitTime ? `${ride.waitTime} minutes` : 'N/A'
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      // Set column widths
      const columnWidths = [
        { wch: 15 }, // Ride ID
        { wch: 15 }, // Status
        { wch: 20 }, // Pickup Date
        { wch: 20 }, // Vehicle Type
        { wch: 25 }, // Organization
        { wch: 25 }, // Transport Company
        { wch: 20 }, // Driver
        { wch: 12 }, // Mileage
        { wch: 40 }, // Pickup Address
        { wch: 40 }, // Destination
        { wch: 20 }, // Cost Center
        { wch: 20 }, // Created At
        { wch: 12 }, // Is Recurring
        { wch: 15 }  // Wait Time
      ];
      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ride Details');
      XLSX.writeFile(workbook, `ride-details-${dayjs().format('YYYY-MM-DD-HHmmss')}.xlsx`);
      message.success(EXPORT_MESSAGES.RIDES_EXPORTED(ridesData?.length));
    } catch (error) {
      message.error(EXPORT_MESSAGES.EXPORT_FAILED);
      console.error('Excel export error:', error);
    }
  };

  // Calculate chart dimensions based on window size
  const getChartDimensions = () => {
    if (windowSize.width < 768) {
      return { width: windowSize.width - 48, height: 300 };
    } else if (windowSize.width < 992) {
      return { width: (windowSize.width - 72) / 2, height: 350 };
    } else {
      return { width: (windowSize.width - 396) / 2, height: 400 };
    }
  };

  const chartDimensions = getChartDimensions();
  const tcPerformanceCompactHeight = Math.max(chartDimensions.height - 80, 220);
  const tcPerformanceContainerHeight = tcPerformanceCompactHeight + 24;
  const geoInsightsSharedHeight = Math.max(chartDimensions.height + 20, 360);
  const cancellationReasonsCompactHeight = Math.max(Math.floor(chartDimensions.height / 2) - 8, 170);
  const overviewUserActivityChartHeight = Math.max(chartDimensions.height - 100, 220);
  const cancellationRidersChartHeight = Math.max(cancellationReasonsCompactHeight + 44, 240);
  const cancellationHoChartHeight = Math.max(cancellationReasonsCompactHeight + 30, 220);
  const cancellationDriverChartHeight = Math.max(cancellationReasonsCompactHeight + 30, 220);
  const cancellationTcChartHeight = Math.max(cancellationReasonsCompactHeight + 30, 220);
  const cancellationStackGap = 16;
  const cancellationCardHeaderHeight = 56;
  const cancellationCardBodyVerticalPadding = 48;
  const cancellationTrendExpandedHeight =
    cancellationRidersChartHeight +
    cancellationHoChartHeight +
    cancellationStackGap +
    cancellationCardHeaderHeight +
    cancellationCardBodyVerticalPadding;
  const totalUserActivity = processedData?.rawData?.length || 0;
  const userActivityRows = useMemo(
    () =>
      Object.entries(processedData?.hoActivityCounts || {})
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count),
    [processedData?.hoActivityCounts]
  );

  const renderUserActivityBars = (rows, containerHeight) => (
    <div className={styles.userActivityWrapper} style={{ maxHeight: containerHeight }}>
      {rows.length === 0 ? (
        <div className={styles.userActivityEmpty}>No activity data available</div>
      ) : (
        rows.map((item, index) => {
          const percentage = totalUserActivity > 0 ? (item.count / totalUserActivity) * 100 : 0;
          return (
            <div key={`${item.label}-${index}`} className={styles.userActivityRow}>
              <div className={styles.userActivityName}>{item.label}</div>
              <div className={styles.userActivityBarLine}>
                <div className={styles.userActivityTrack}>
                  <div
                    className={styles.userActivityFill}
                    style={{
                      width: `${Math.min(percentage, 100)}%`,
                      backgroundColor: stringToColor(item.label),
                    }}
                  />
                </div>
                <div className={styles.userActivityPercent}>{percentage.toFixed(1)}%</div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const cancellationPalettes = {
    RIDER: ['#3E5A87', '#FF8226', '#2E84FD', '#39DE82', '#D9DAE0', '#8B5CF6', '#F59E0B', '#14B8A6'],
    HO_ADMIN: ['#3E5A87', '#FF8226', '#2E84FD', '#39DE82', '#D9DAE0', '#8B5CF6', '#F59E0B', '#14B8A6'],
    DRIVER: ['#3E5A87', '#FF8226', '#2E84FD', '#39DE82', '#D9DAE0', '#8B5CF6', '#F59E0B', '#14B8A6'],
    TC_ADMIN: ['#3E5A87', '#FF8226', '#2E84FD', '#39DE82', '#D9DAE0', '#8B5CF6', '#F59E0B', '#14B8A6'],
  };

  const getCancellationReasonSeries = (roleKey) => {
    const palette = cancellationPalettes[roleKey] || [];
    return Object.entries(processedData?.cancellationData?.[roleKey] || {})
      .map(([_, obj]) => ({
        label: obj.reason,
        value: obj.count,
      }))
      .filter((item) => item.value > 0)
      .map((item, index) => ({
        ...item,
        color: palette[index % palette.length],
      }));
  };

  // Status optionsa
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'CANCELED', label: 'Canceled' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'EXPIRED', label: 'Expired' },
    { value: 'UNABLE_TO_FULFILL', label: 'Unable to fullfill' }
  ];

  const vehicleTypeOptions = useMemo(() => {
    if (!ridesData || ridesData?.length === 0) return [{ value: 'all', label: 'All Types' }];

    const types = new Set();
    ridesData.forEach(ride => {
      if (ride.vehicleType) types.add(ride.vehicleType);
    });

    const orderedKnown = [
      VEHICLE_TYPES.UBER_AMBULATORY_RIDE,
      ...VEHICLE_OPTIONS.filter((o) => o.value !== VEHICLE_TYPES.UBER_AMBULATORY_RIDE).map((o) => o.value),
    ];
    const sortedTypes = [
      ...orderedKnown.filter((v) => types.has(v)),
      ...Array.from(types).filter((v) => !orderedKnown.includes(v)),
    ];

    return [
      { value: 'all', label: 'All Types' },
      ...sortedTypes.map((type) => ({
        value: type,
        label: getDetailedViewVehicleTypeFilterLabel(type),
      }))
    ];
  }, [ridesData]);

  const detailedViewDisplayedCount = useMemo(() => {
    let data = ridesData || [];
    if (detailedViewFilters?.status?.length) {
      data = data.filter((ride) => detailedViewFilters.status.includes(ride.status));
    }
    if (detailedViewFilters?.vehicleType?.length) {
      data = data.filter((ride) => detailedViewFilters.vehicleType.includes(ride.vehicleType));
    }
    return data.length;
  }, [ridesData, detailedViewFilters]);

  const hasDetailedViewTableFilters = Boolean(
    detailedViewFilters?.status?.length || detailedViewFilters?.vehicleType?.length
  );

  // Show ride details modal
  const showRideDetails = (ride) => {
    setSelectedRide(ride);
    setRideModalVisible(true);
  };

  // if (!processedData) {
  //   return (
  //     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
  //       <Spin size="large" tip="Loading dashboard..." />
  //     </div>
  //   );
  // }

  return (
    <Layout className="dashboard-layout" >
      <Header style={{
        background: '#f5f6f5',
        padding: '14px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        rowGap: '12px',
        borderBottom: '1px solid #e8e8e8',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        height: 'auto',
        lineHeight: 'normal'
      }}>
        {/* Title + subtitle */}
        <div className={styles.headerTitleSection}>
          <div className={styles.headerTitleRow}>
            <h2 className={styles.dashboardTitle}>
              <span className={styles.codiAiGradient}>Codi AI</span><sup className={styles.titleSuperscriptAi}><CodoAIIcon width={10} height={10} fill="#2FA770" /></sup>
              <span className={styles.titleInsightsText}>Insights</span><sup className={styles.titleSuperscriptBeta}><span className={styles.betaBadge}>BETA</span></sup>
            </h2>
          </div>
          <p className={styles.headerSubtitle}>
            A unified view of key metrics &amp; operational trends.
          </p>
        </div>

        {/* Inline filter container — grows with header row */}
        <div className={styles.headerFilterBarWrap}>
        <div className={styles.filterContainer}>
          {/* Filters label */}
          <div
            className={`${styles.filterSection} ${styles.filterSectionStatic}`}
          >
            <FiltersIcon width={16} height={16} />
            <span className={styles.filterSectionText}>Filters</span>
          </div>

          <div className={styles.filterDivider} />

          {/* Date Range */}
          <div className={`${styles.filterSection} ${styles.filterSectionStatic}`}>
            <CalendarIcon className={styles.filterSectionIcon} width={16} height={16} stroke="#047857" />
            <div className={styles.filterControlColumn}>
              <span className={styles.filterLabel}>Date Range</span>
              <Tooltip title={insightsDateRangeTitle || undefined}>
                <span className={styles.filterDateRangeTooltipWrap}>
                  <RangePicker
                    classNames={{ popup: { root: styles.insightsDateRangeDropdown } }}
                    dropdownAlign={insightsDateRangeDropdownAlign}
                    value={dateRange}
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        setDateRange(dates);
                      } else if (!dates) {
                        setDateRange(null);
                      }
                    }}
                    onCalendarChange={(dates) => {
                      if (dates && dates[0] && !dates[1]) {
                        setDateRange([dates[0], null]);
                      }
                    }}
                    disabledDate={disabledDate}
                    variant="borderless"
                    className={styles.filterRangePicker}
                    format="MMM D, YYYY"
                    separator=" - "
                    size="small"
                    suffixIcon={<DownOutlined />}
                    allowClear={false}
                    allowEmpty={[true, true]}
                  />
                </span>
              </Tooltip>
            </div>
          </div>

          {(userRole === USER_ROLES.ENTERPRISE_ADMIN || 
            (userRole === USER_ROLES.HO_ADMIN && healthOrganizations.length > 1)) && (
            <>
              <div className={styles.filterDivider} />
              <div className={`${styles.filterSection} ${styles.filterSectionStatic} ${styles.filterSectionCompany}`}>
                <OtherOrganizationIcon className={styles.filterSectionIcon} width={16} height={16} stroke="#047857" />
                <div className={`${styles.filterControlColumn} ${styles.filterControlOrg}`}>
                  <span className={styles.filterLabel}>Organization</span>
                  <Popover
                    open={orgPopoverVisible}
                    onOpenChange={setOrgPopoverVisible}
                    trigger="click"
                    placement="bottomLeft"
                    classNames={{ root: styles.superAdminOrgPopoverOverlay }}
                    content={
                      <div data-nested-checkbox-filter-root data-insights-org-filter onMouseDown={e => e.stopPropagation()}>
                        <GroupedCheckboxFilterMenu
                          sections={enterpriseChildOrgSections}
                          selectedItems={selectedOrgItems}
                          onChange={(items) => {
                            setSelectedOrgItems(items);
                            setSelectedOrg(items.map((o) => o.id));
                          }}
                          fieldName="healthOrganizations"
                          checkboxClassName={menuStyles.menuCheckbox}
                        />
                      </div>
                    }
                  >
                    <div className={styles.orgFilterTrigger}>
                      <div className={styles.orgFilterTriggerContent}>
                        {selectedOrgItems.length === 0 ? (
                          <span className={styles.orgFilterPlaceholder}>All Healthcare Systems</span>
                        ) : (
                          <>
                            {selectedOrgItems.slice(0, 1).map(item => (
                              <span key={item.id} className={styles.orgSelectTag}>
                                <span className={styles.orgSelectTagInner}>{item.name}</span>
                                <span
                                  className={styles.orgSelectTagClose}
                                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                                  onClick={e => {
                                    e.stopPropagation();
                                    setSelectedOrgItems(prev => {
                                      const next = prev.filter(o => o.id !== item.id);
                                      setSelectedOrg(next.map(o => o.id));
                                      return next;
                                    });
                                  }}
                                >×</span>
                              </span>
                            ))}
                            {selectedOrgItems.length > 1 && (
                              <span className={styles.orgSelectTag}>+{selectedOrgItems.length - 1}...</span>
                            )}
                          </>
                        )}
                      </div>
                      <DownOutlined className={styles.filterValueChevron} />
                    </div>
                  </Popover>
                </div>
              </div>
            </>
          )}

          {!viewComponents && userRole !== USER_ROLES.ENTERPRISE_ADMIN && userRole !== USER_ROLES.HO_ADMIN && (
            <>
              <div className={styles.filterDivider} />
              <div className={`${styles.filterSection} ${styles.filterSectionStatic} ${styles.filterSectionCompany}`}>
                <OtherOrganizationIcon className={styles.filterSectionIcon} width={16} height={16} stroke="#047857" />
                <div className={`${styles.filterControlColumn} ${styles.filterControlOrg}`}>
                  <span className={styles.filterLabel}>Organization</span>
                  <Popover
                    open={orgPopoverVisible}
                    onOpenChange={setOrgPopoverVisible}
                    trigger="click"
                    placement="bottomLeft"
                    classNames={{ root: styles.superAdminOrgPopoverOverlay }}
                    content={
                      <div data-nested-checkbox-filter-root data-insights-org-filter onMouseDown={e => e.stopPropagation()}>
                        <GroupedCheckboxFilterMenu
                          sections={superAdminOrgSections}
                          selectedItems={selectedOrgItems}
                          onChange={(items) => {
                            setSelectedOrgItems(items);
                            setSelectedOrg(items.map((o) => o.id));
                          }}
                          fieldName="healthOrganizations"
                          checkboxClassName={menuStyles.menuCheckbox}
                        />
                      </div>
                    }
                  >
                    <div className={styles.orgFilterTrigger}>
                      <div className={styles.orgFilterTriggerContent}>
                        {selectedOrgItems.length === 0 ? (
                          <span className={styles.orgFilterPlaceholder}>All Healthcare Systems</span>
                        ) : (
                          <>
                            {selectedOrgItems.slice(0, 1).map(item => (
                              <span key={item.id} className={styles.orgSelectTag}>
                                <span className={styles.orgSelectTagInner}>{item.name}</span>
                                <span
                                  className={styles.orgSelectTagClose}
                                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                                  onClick={e => {
                                    e.stopPropagation();
                                    setSelectedOrgItems(prev => {
                                      const next = prev.filter(o => o.id !== item.id);
                                      setSelectedOrg(next.map(o => o.id));
                                      return next;
                                    });
                                  }}
                                >×</span>
                              </span>
                            ))}
                            {selectedOrgItems.length > 1 && (
                              <span className={styles.orgSelectTag}>+{selectedOrgItems.length - 1}...</span>
                            )}
                          </>
                        )}
                      </div>
                      <DownOutlined className={styles.filterValueChevron} />
                    </div>
                  </Popover>
                </div>
              </div>
            </>
          )}

          {userRole === USER_ROLES.SUPER_ADMIN && (
            <>
              <div className={styles.filterDivider} />
              <div className={`${styles.filterSection} ${styles.filterSectionStatic} ${styles.filterSectionCompany}`}>
                <CarOutlined className={styles.filterSectionIcon} />
                <div className={`${styles.filterControlColumn} ${styles.filterControlCompany}`}>
                  <span className={styles.filterLabel}>Company</span>
                  <Select
                    value={selectedTransport}
                    onChange={setSelectedTransport}
                    variant="borderless"
                    placement="bottomLeft"
                    builtinPlacements={insightsFilterSelectBuiltinPlacements}
                    className={styles.filterSelectCompany}
                    classNames={{ popup: { root: styles.insightsFilterDropdownCompany } }}
                    popupMatchSelectWidth={false}
                    title={insightsFilterCompanyTitle}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    labelRender={({ label }) => (
                      <span className={styles.orgFilterPlaceholder}>{label}</span>
                    )}
                    suffixIcon={<DownOutlined className={styles.filterValueChevron} />}
                  >
                    <Option value="all" label="All Companies">All Companies</Option>
                    {transportCompanies.map(tc => (
                      <Option key={tc.id} value={tc.id} label={tc.name}>{tc.name}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            </>
          )}

          <div className={styles.filterDivider} />

          {/* Refresh */}
          <Tooltip title="Refresh">
            <div className={styles.refreshSection}>
              <button
                type="button"
                className={styles.refreshIconButton}
                onClick={handleRefreshFilters}
                aria-label="Refresh data"
              >
                <ReloadIcon width={18} height={18} />
              </button>
            </div>
          </Tooltip>
        </div>
        </div>
      </Header>

      <Content style={{ margin: '24px' }}>
        {/* Filters - now inline in header */}
        {false && (
          <Card
            style={{ marginBottom: 24 }}
            title={
              <Space>
                <FiltersIcon width={25} height={25} />
                <span>Filters</span>
              </Space>
            }
          >
            <Space size="large" wrap style={{ width: '100%' }}>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange(dates);
                  } else if (!dates) {
                    setDateRange(null);
                  }
                }}
                onCalendarChange={(dates) => {
                  // Allow updating when both dates are selected
                  if (dates && dates[0] && dates[1]) {
                    setDateRange(dates);
                  }
                }}
                style={{ width: '100%', maxWidth: 300 }}
                allowClear={false}
                allowEmpty={[true, true]}
              />
              {/* <Button
                onClick={() => setDateRange([])}
                style={{ marginLeft: 8 }}
              >
                Reset Dates
              </Button> */}

              {((userRole === USER_ROLES.ENTERPRISE_ADMIN || 
                (userRole === USER_ROLES.HO_ADMIN && healthOrganizations.length > 1)
              ) || !viewComponents) && (

                <Select
                  placeholder="Select Health Organization"
                  style={{ width: 250 }}
                  value={selectedOrg}
                  onChange={setSelectedOrg}
                  suffixIcon={<OtherOrganizationIcon width={14} height={14} stroke="#8c8c8c" />}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="all">All Organizations</Option>
                  {healthOrganizations.map(org => (
                    <Option key={org.id} value={org.id}>{org.name}</Option>
                  ))}
                </Select>
              )}

              {userRole === USER_ROLES.SUPER_ADMIN && (
                <Select
                  placeholder="Select Transport Company"
                  style={{ width: 250 }}
                  value={selectedTransport}
                  onChange={setSelectedTransport}
                  suffixIcon={<CarOutlined />}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="all">All Companies</Option>
                  {transportCompanies.map(tc => (
                    <Option key={tc.id} value={tc.id}>{tc.name}</Option>
                  ))}
                </Select>

              )}
              {/* <Select
                placeholder="Select Status"
                style={{ width: 200 }}
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={statusOptions}
              />
              <Select
                placeholder="Select Vehicle Type"
                style={{ width: 200 }}
                value={selectedVehicleType}
                onChange={setSelectedVehicleType}
                options={vehicleTypeOptions}
              />
              <Select
                placeholder="Select Driver"
                style={{ width: 200 }}
                value={selectedDriver}
                onChange={setSelectedDriver}
                options={driverOptions}
                disabled={drivers.length === 0}
              /> */}
            </Space>
          </Card>
        )}


        {/* Overview */}
        <Card className={styles.overviewCard}>
          <div className={styles.overviewRow}>
            <div className={styles.overviewAiLabelRow}>
              <div className={styles.aiOverviewSparkles} aria-hidden="true">
                <MedhaulStartIcon className={styles.aiSparkleIcon} width={20} height={20} />
              </div>
              <span className={styles.aiLabel}>AI</span>
              <span className={`${styles.codiAiGradient} ${styles.overviewAiGradientLg}`}>Overview</span>
            </div>
            <span className={styles.overviewText}>
              {ridesData.length > 0 ? (
                <>
                  During the selected period, {ridesData.length} rides were entered into the MedHaul platform, with {processedData.statusCounts.CANCELED || 0} canceled by a member of the care team or the rider. A total of {processedData.statusCounts.COMPLETED || 0} rides were completed.
                  {processedData?.uniquePatientCount > 0 && (
                    <>
                      {' '}{processedData.uniquePatientCount} members were transported within the MedHaul network
                      {processedData?.timeMetrics?.avgMileage && processedData.timeMetrics.avgMileage !== 'N/A' && (
                        <>, with an average trip distance of {processedData.timeMetrics.avgMileage}</>
                      )}
                      .
                    </>
                  )}
                  {Object.keys(processedData?.hoActivityCounts || {}).length > 0 && (
                    <>
                      {' '}The most active users were{' '}
                      {Object.entries(processedData.hoActivityCounts)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 2)
                        .map(([name], idx, arr) => (
                          <React.Fragment key={name}>
                            {name}{idx === 0 && arr.length > 1 ? ' and ' : ''}
                          </React.Fragment>
                        ))
                      }, who together accounted for a significant portion of overall activity.
                    </>
                  )}
                  {processedData?.satisfactionScore > 0 && (
                    <>{' '}The member satisfaction score is {processedData.satisfactionScore.toFixed(1)}.</>
                  )}
                </>
              ) : (
                'No ride data available for the selected filters. Please adjust your date range or filters to view summary information.'
              )}
            </span>
          </div>
        </Card>

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {/* Rides Completed */}
          <Col xs={24} sm={12} md={8} lg={4} style={{ marginBottom: windowSize.width < 768 ? 16 : 0 }}>
            <Card className={styles.metricCardCompleted}>


              <Statistic
                className={styles.statisticFixed}
                title={(
                  <span className={styles.metricTitleWithIcon}>
                    <RidesCompletedIcon width={16} height={16} />
                    Rides Completed
                  </span>
                )}
                value={processedData?.statusCounts?.COMPLETED || 0}
                styles={{ content: {
                  color: '#52c41a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexWrap: 'wrap',              // Adds small spacing between icon and value
                } }}
              // suffix={
              //   <span className={styles.summaryPercentage}>
              //     {ridesData?.length > 0
              //       ? `${Math.round((processedData.statusCounts.COMPLETED / ridesData.length) * 100)}% completion rate`
              //       : 'No rides data'
              //     }
              //   </span>
              // }
              />



            </Card>
          </Col>

          {/* Rides Canceled */}
          <Col xs={24} sm={12} md={8} lg={4} style={{ marginBottom: windowSize.width < 768 ? 16 : 0 }}>
            <Card className={styles.metricCardCanceled}>


              <Statistic
                className={styles.statisticFixed}
                title={(
                  <span className={styles.metricTitleWithIcon}>
                    <RideCancelledIcon width={16} height={16} />
                    Rides Canceled
                  </span>
                )}
                value={(processedData?.statusCounts?.Expired || 0) + (processedData?.statusCounts?.CANCELED || 0)}
                styles={{ content: {
                  color: '#f5222d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexWrap: 'wrap',             // Adds small spacing between icon and value
                } }}
              // suffix={
              //   <span className={styles.summaryPercentage}>
              //     {ridesData.length > 0 ?
              //       `${Math.round(((processedData.statusCounts.CANCELED) / ridesData.length) * 100)}% cancellation rate` :
              //       'No rides data'}
              //   </span>
              // }
              />



            </Card>
          </Col>

          {/* Total Members Served */}
          <Col xs={24} sm={12} md={8} lg={4} style={{ marginBottom: windowSize.width < 768 ? 16 : 0 }}>
            <Card className={styles.metricCardTotalMembers}>
              <Statistic
                className={styles.statisticFixed}
                title={(
                  <span className={styles.metricTitleWithIcon}>
                    <TotalMemberedServeIcon width={16} height={16} />
                    Total Riders
                  </span>
                )}
                value={processedData?.uniqueMembers?.totalMembers || 0}
                styles={{ content: {
                  color: '#1890ff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexWrap: 'wrap',               // Adds small spacing between icon and value
                } }}
              />
              <div className={styles.metricCardFootnote}>
                Across all rides
              </div>

            </Card>
          </Col>

          {/* Unique Members Served */}
          <Col xs={24} sm={12} md={8} lg={4} style={{ marginBottom: windowSize.width < 900 ? 16 : 0 }}>
            <Card className={styles.metricCardUniqueMembers}>
              <Statistic
                className={styles.statisticFixed}
                title={(
                  <span className={styles.metricTitleWithIcon}>
                    <UniqueMemberServedIcon width={16} height={16} />
                    New Riders
                  </span>
                )}
                value={processedData?.uniqueMembers?.uniqueCount || 0}
                styles={{ content: {
                  color: '#722ed1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexWrap: 'wrap',              // Adds small spacing between icon and value
                } }}
              />
              <div className={`${styles.metricCardFootnote} ${styles.metricCardFootnoteUnique}`}>
                {processedData?.uniqueMembers?.totalMembers > 0 ? (
                  `${Math.round((processedData?.uniqueMembers.uniqueCount / processedData?.uniqueMembers.totalMembers) * 100)}% of total`
                ) : 'No member data'}
              </div>

            </Card>
          </Col>

          {/* Rider No-Shows */}
          <Col xs={24} sm={12} md={8} lg={4} style={{ marginBottom: windowSize.width < 768 ? 16 : 0 }}>
            <Card className={styles.metricCardNoShows}>
              <Statistic
                className={styles.statisticFixed}
                title={(
                  <span className={styles.metricTitleWithIcon}>
                    <RiderNoShowIcon width={16} height={16} />
                    Rider No-Shows
                  </span>
                )}
                value={processedData?.noShowsCount || 0}
                styles={{ content: {
                  color: '#faad14',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexWrap: 'wrap',              // Adds small spacing between icon and value
                } }}
              // suffix={
              //   <span className={styles.summaryPercentage}>
              //     {ridesData.length > 0 ? (
              //       `${processedData?.noShowRatio}% no-show rate`
              //     ) : 'No rides data'}
              //   </span>
              // }

              />

            </Card>
          </Col>

          {/* Member Satisfaction — icon + score in one row, stars on next line */}
          <Col xs={24} sm={12} md={8} lg={4} style={{ marginBottom: windowSize.width < 768 ? 16 : 0 }}>
            <Card className={styles.metricCardSatisfaction}>
              <div className={`${styles.metricTitleWithIcon} ${styles.metricTitleMuted}`}>
                <MemberSatisfactionIcon width={16} height={16} />
                Rider Rating
              </div>
              {userRole === USER_ROLES.ENTERPRISE_ADMIN || selectedOrg.length > 0 ? (
                <div className={styles.satisfactionRow}>
                  <span className={styles.satisfactionScore}>
                    {processedData?.satisfactionScore?.toFixed(1) ?? '0.0'}
                    <span className={styles.satisfactionScoreSuffix}>/5</span>
                  </span>
                  <div className={styles.satisfactionStarsCol}>
                    <div className={styles.satisfactionStarRow}>
                      {renderStarRating(processedData?.satisfactionScore ?? 0, 12)}
                    </div>
                    <div className={styles.satisfactionRatingCount}>
                      {processedData?.satisfactionResponses ?? 0} Ratings
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.satisfactionRow}>
                  <span className={styles.satisfactionHint}>Select an organization to view score</span>
                </div>
              )}
            </Card>
          </Col>

        </Row>

        {/* Main Dashboard Content */}
        <Tabs
          className={styles.dashboardTabs}
          defaultActiveKey="1"
          activeKey={activeTab}
          onChange={setActiveTab}
          destroyOnHidden={false}
          items={[
            {
              key: '1',
              label: (
                <span className={styles.tabLabel}>
                  <OverviewIcon width={17} height={17} />
                  Overview
                </span>
              ),
              children: (
                <React.Suspense fallback={<Spin />}>
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Ride Status
                            <Tooltip title="Ride status distribution across all rides">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setRideStatusModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >

                        <div style={{ height: chartDimensions.height }}>
                          <BarChart
                            data={Object.entries(processedData?.statusCounts || {}).map(([status, count]) => ({
                              status: statusLabel[status] || status,
                              count,
                              _statusKey: status,
                            }))}
                            xField="status"
                            yFields={[{ key: 'count', label: 'Rides' }]}
                            colors={Object.entries(processedData?.statusCounts || {}).map(([status]) => statusColors[status] || '#8c8c8c')}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                          />
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Utilization
                            <Tooltip title="Utilization trends over time">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setUtilizationModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >
                        <div style={{ height: chartDimensions.height }}>
                          <MonthlyTrendChart
                            data={processedData?.monthlyTrends}
                            xField="month"
                            yFields={rideStatuses}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                          />
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Vehicle Types
                            <Tooltip title="Vehicle types distribution across rides">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        //  loading={loading}
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setVehicleTypeModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >
                        <div style={{ height: overviewUserActivityChartHeight, width: '100%', minWidth: 0 }}>

                          <VehicleTypeChart
                            data={Object.entries(processedData?.vehicleTypeCounts).map(([status, count]) => ({
                              label: VEHICLE_TYPES_LABEL[status] || status,
                              key: status,
                              value: count,
                              color: vehicleTypesColors[status.trim()] || '#d9d9d9'
                            }))}
                            width={chartDimensions.width}
                            height={overviewUserActivityChartHeight}
                            onSegmentClick={(segment) => {
                              // setSelectedVehicleType(segment.label);
                              //setActiveTab('3'); // Switch to performance tab
                            }}
                          />
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            <span className={styles.userActivityTitle}>User Activity (Health Partners)</span>
                            <Tooltip title="Total HO team members who have scheduled rides">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        //   loading={loading}
                        extra={
                          <div className={styles.userActivityHeaderRight}>
                            <span className={styles.userActivityTotal}>Total: {totalUserActivity}</span>
                            <Tooltip title="Expand chart">
                              <button
                                type="button"
                                onClick={() => setUserActivityModalVisible(true)}
                                className={styles.iconButton}
                              >
                                <EnlargeIcon />
                              </button>
                            </Tooltip>
                          </div>
                        }
                      >
                        <div style={{ height: overviewUserActivityChartHeight }}>
                          {renderUserActivityBars(userActivityRows, overviewUserActivityChartHeight)}
                        </div>
                      </Card>
                    </Col>
                  </Row>


                </React.Suspense>
              )
            },
            {
              key: '2',
              disabled: [USER_ROLES.ENTERPRISE_ADMIN, USER_ROLES.HO_ADMIN, USER_ROLES.HO_EMPLOYEE].includes(userRole || authorizationRole),
              label: (
                <span className={styles.tabLabel}>
                  <AccessIcon width={17} height={17} />
                  Access
                </span>
              ),
              children: (
                <React.Suspense fallback={<Spin />}>
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Rider Metrics
                            <Tooltip title={
                              <>
                                <div><strong>Mobility Indicators</strong></div>
                                <div>- No shows &gt; 5 within 90 days</div>
                                <div>- Cancellations &gt; 5 within 90 days</div>
                                <div>- Rider Age &gt; 70</div>
                                <div>- Rider Weight &gt; 300 lbs</div>
                              </>
                            }>
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        //  loading={loading}
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setRiderMetricsModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >

                        <div style={{ height: chartDimensions.height }}>

                          {/* <MonthlyTrendChart
                            data={processedData?.monthlyPatientTrends}
                            xField="month"
                            yFields={[{
                              key: 'allPatients', label: 'All Patients', color: '#1890ff'
                            },
                            { key: 'specialAccommodations', label: 'Special Accommodations', color: '#ff4d4f' },

                            ]}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                          /> */}

                          <BarChart
                            data={processedData?.monthlyPatientTrends}
                            xField="month"
                            yFields={[{
                              key: 'allPatients', label: 'All Patients', color: '#379B63'
                            },
                            { key: 'specialAccommodations', label: 'Mobility Indicators', color: '#A1F4D8' },

                            ]}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                            chartStyle="riderMetrics"
                            onBarClick={(data) => {

                            }} />

                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Risk Analysis
                            <Tooltip title="All patients' vs Mobility Indicators vs Frequent Riders">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        //  loading={loading}
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setRiskAnalysisModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >
                        <div style={{ height: chartDimensions.height }}>
                          {/* <MonthlyTrendChart
                            data={processedData?.monthlyPatientTrends}
                            xField="month"
                            yFields=
                            {[{
                              key: 'riderNoShow', label: 'Rider No Show', color: '#1890ff'
                            },
                            { key: 'specialAccommodations', label: 'Special Accommodations', color: '#ff4d4f' },

                            { key: 'frequentScheduled', label: ' Frequent Riders', color: '#52c41a' }
                            ]}

                            width={chartDimensions.width}
                            height={chartDimensions.height}
                          /> */}

                          <BarChart
                            data={processedData?.monthlyPatientTrends}
                            xField="month"
                            yFields=
                            {[{
                              key: 'riderNoShow', label: 'Rider No Show', color: '#3E5A87'
                            },
                            { key: 'specialAccommodations', label: 'Mobility Indicators', color: '#2E84FD' },

                            { key: 'frequentScheduled', label: 'Frequent Riders', color: '#9864E7' }
                            ]}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                            chartStyle="riderMetrics"
                            onBarClick={(data) => {

                            }} />
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Rider Outreach (Rural rides vs Non rural rides)
                            <Tooltip title="Rider Outreach (Rural rides vs Non rural rides)">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        //  loading={loading}
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setRiderMetricsModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >
                        <div style={{ height: chartDimensions.height }}>
                          {/* <MonthlyTrendChart
                            data={processedData?.monthlyPatientTrends}
                            xField="month"
                            yFields={rideDistanceCategories}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                          /> */}


                          <BarChart
                            data={processedData?.monthlyPatientTrends}
                            xField="month"
                            yFields={rideDistanceCategories}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                            chartStyle="riderMetrics"
                            onBarClick={(data) => {

                            }} />
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      {/* <Card 
                        title="Time Metrics" 
                     //   loading={loading}
                        extra={
                          <Tooltip title="Response, load, and ride duration metrics">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ height: chartDimensions.height }}>
                          <TimeMetricsChart 
                            data={processedData?.timeMetrics}
                            width={chartDimensions.width}
                            height={chartDimensions.height}
                          />
                        </div>
                      </Card> */}
                    </Col>
                  </Row>
                </React.Suspense>
              )
            },
            {
              key: '3',
              label: (
                <span className={styles.tabLabel}>
                  <DetailedViewIcon width={17} height={17} />
                  Detailed View
                </span>
              ),
              children: (
                <Card
                  title="Ride Details"
                  //  loading={loading}
                  extra={
                    <Space
                      className={styles.detailedViewRideDetailsExtra}
                      align="center"
                      wrap={false}
                    >
                      <span>
                        Showing {detailedViewDisplayedCount} rides
                      </span>

                      {hasDetailedViewTableFilters && (
                        <button
                          type="button"
                          className={styles.detailedViewClearFiltersBtn}
                          onClick={() => setDetailedViewFilters({})}
                        >
                          <span className={styles.detailedViewClearFiltersCrossWrap} aria-hidden>
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              focusable="false"
                              className={styles.detailedViewClearFiltersCrossSvg}
                            >
                              <path
                                d="M3 3l6 6M9 3l-6 6"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.15"
                                strokeLinecap="round"
                              />
                            </svg>
                          </span>
                          <span className={styles.detailedViewClearFiltersLabel}>Clear Filters</span>
                        </button>
                      )}
                    </Space>
                  }
                >
                  <Table
                    className={styles.detailedViewRidesTable}
                    style={{
                      '--dv-sort-caret-icon': `url(${JSON.stringify(SORT_CARET_DATA_URI)})`,
                    }}
                    dataSource={ridesData}
                    rowKey="id"
                    scroll={{ x: true }}
                    pagination={{
                      pageSize: detailedViewPageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '25', '50', '100'],
                      onShowSizeChange: (current, size) => { setDetailedViewPageSize(size); }
                    }}
                    onChange={(_, filters) => {
                      setDetailedViewFilters({
                        status: filters.status || null,
                        vehicleType: filters.vehicleType || null,
                      });
                    }}
                    onRow={(record) => ({
                      onClick: () => showRideDetails(record)
                    })}
                    columns={[
                      {
                        title: 'Ride ID',
                        dataIndex: 'seqId',
                        key: 'seqId',
                        sorter: (a, b) => a.seqId.localeCompare(b.seqId),
                        fixed: 'left',
                        onCell: () => ({ className: styles.detailedViewRideIdCell })
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        width: 180,
                        filters: statusOptions.filter(opt => opt.value !== 'all').map(opt => ({ text: opt.label, value: opt.value })),
                        filteredValue: detailedViewFilters.status || null,
                        onFilter: (value, record) => record.status === value,
                        sorter: (a, b) => a.status.localeCompare(b.status),
                        onHeaderCell: () => ({
                          className: styles.detailedViewStatusHeader,
                        }),
                        filterIcon: (filtered) => (
                          <SortIcon
                            width={18}
                            height={18}
                            stroke={filtered ? '#94A3B8' : '#94A3B8'}
                          />
                        ),
                        render: (status) => {
                          const label =
                            statusLabel[status] ??
                            statusOptions.find((o) => o.value === status)?.label ??
                            status;
                          const pillColor = getDetailedViewStatusColor(status);
                          return (
                            <span
                              className={styles.detailedViewStatusPill}
                              style={{
                                color: pillColor,
                                borderColor: pillColor,
                              }}
                            >
                              {label}
                            </span>
                          );
                        }
                      },
                      {
                        title: 'Pickup Date',
                        dataIndex: 'pickUpDate',
                        key: 'pickUpDate',
                        render: (date) => {
                          if (!date) {
                            return <span className={styles.detailedViewPickupDate}>N/A</span>;
                          }
                          const d = dayjs(date);
                          if (!d.isValid()) {
                            return <span className={styles.detailedViewPickupDate}>N/A</span>;
                          }
                          return (
                            <div className={styles.detailedViewPickupDate}>
                              <span className={styles.detailedViewPickupDateLine}>
                                {d.format('MMM D, YYYY')}
                              </span>
                              <span className={styles.detailedViewPickupTimeLine}>
                                {d.format('h:mm A')}
                              </span>
                            </div>
                          );
                        },
                        sorter: (a, b) => new Date(a.pickUpDate) - new Date(b.pickUpDate)
                      },
                      {
                        title: 'Vehicle Type',
                        dataIndex: 'vehicleType',
                        key: 'vehicleType',
                        width: 200,
                        align: 'center',
                        onCell: () => ({ className: styles.detailedViewVehicleTypeCell }),
                        filters: vehicleTypeOptions.filter(opt => opt.value !== 'all').map(opt => ({ text: opt.label, value: opt.value })),
                        filteredValue: detailedViewFilters.vehicleType || null,
                        onFilter: (value, record) => record.vehicleType === value,
                        filterIcon: (filtered) => (
                          <SortIcon
                            width={18}
                            height={18}
                            stroke={filtered ? '#94A3B8' : '#94A3B8'}
                          />
                        ),
                        render: (vehicleType) => {
                          const display = getDetailedViewVehicleTypeDisplay(vehicleType);
                          const fullLabel = getDetailedViewVehicleTypeFullLabel(vehicleType);
                          return (  
                            <div className={styles.detailedViewVehicleTypeCellContent}>
                              <Tooltip
                                title={fullLabel}
                                classNames={{ root: styles.detailedViewVehicleTypeTooltip }}
                              >
                                <span
                                  className={classNames(styles.detailedViewVehicleTypePill, {
                                    [styles.detailedViewVehicleTypePillStacked]: display.secondary,
                                  })}
                                >
                                {display.isUberHealth ? (
                                  <span className={styles.detailedViewVehicleTypeUberRow}>
                                    <img
                                      src={UberHealthIcon}
                                      alt=""
                                      className={styles.detailedViewUberHealthIcon}
                                    />
                                    <span className={styles.detailedViewVehicleTypePrimary}>
                                      {display.primary}
                                    </span>
                                  </span>
                                ) : (
                                  <>
                                    <span className={styles.detailedViewVehicleTypePrimary}>
                                      {display.primary}
                                    </span>
                                    {display.secondary && (
                                      <span className={styles.detailedViewVehicleTypeSecondary}>
                                        {display.secondary}
                                      </span>
                                    )}
                                  </>
                                )}
                              </span>
                              </Tooltip>
                            </div>
                          );
                        }
                      },
                      {
                        title: 'Organization',
                        dataIndex: 'healthOrganizationId',
                        key: 'healthOrganizationId',
                        render: (id, record) => {
                          const raw = getOrganizationName(record, healthOrganizations);
                          const lines = getDetailedViewOrganizationLines(raw);
                          return (
                            <div
                              className={classNames(
                                styles.detailedViewOrgCell,
                                styles.detailedViewOrgTransportLineGap
                              )}
                            >
                              {lines.map((line, idx) => (
                                <span
                                  key={idx}
                                  className={styles.detailedViewOrgLine}
                                >
                                  {line}
                                </span>
                              ))}
                            </div>
                          );
                        }
                      },
                      ...(!hideTransportCompanyAndDriver ? [
                        {
                          title: 'Transport Co.',
                          dataIndex: 'transportCompanyId',
                          key: 'transportCompanyId',
                          render: (id, record) => {
                            const raw = getTransportCompanyName(record, transportCompanies);
                            const isCoFallback = raw === 'N/A';
                            const lines = getDetailedViewFirstTwoWordsLines(raw);
                            return (
                              <div
                                className={classNames(
                                  styles.detailedViewOrgCell,
                                  styles.detailedViewOrgTransportLineGap,
                                  isCoFallback && styles.detailedViewTransportCoFallback
                                )}
                              >
                                {lines.map((line, idx) => (
                                  <span
                                    key={idx}
                                    className={styles.detailedViewOrgLine}
                                  >
                                    {line}
                                  </span>
                                ))}
                              </div>
                            );
                          }
                        },
                        {
                          title: 'Driver',
                          dataIndex: 'driverName',
                          key: 'driverName',
                          render: (driverName) => {
                            const text =
                              driverName != null && driverName !== ''
                                ? String(driverName)
                                : 'N/A';
                            return (
                              <div className={styles.detailedViewOrgCell}>
                                <span className={styles.detailedViewOrgLine}>
                                  {text}
                                </span>
                              </div>
                            );
                          }
                        },
                      ] : []),
                      {
                        title: 'Mileage',
                        dataIndex: 'actMileage',
                        key: 'actMileage',
                        render: (mileage) => (
                          <span className={styles.detailedViewMileageCell}>
                            {mileage ? `${mileage} miles` : 'N/A'}
                          </span>
                        )
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        fixed: 'right',
                        render: (_, record) => (
                          <Tooltip title="View details">
                            <button
                              type="button"
                              className={styles.iconButton}
                              aria-label="View ride details"
                              onClick={(e) => {
                                e.stopPropagation();
                                showRideDetails(record);
                              }}
                            >
                              <InfoIcon width={18} height={18} />
                            </button>
                          </Tooltip>
                        )
                      }
                    ]}
                  />
                  <Button
                    className={classNames({
                      [styles.btn]: true,
                      [styles.customBtn]: true,
                    })}
                    type="primary"

                    onClick={exportDetailedViewToExcel}
                    disabled={!ridesData || ridesData.length === 0}
                  >
                    Export Report
                  </Button>
                </Card>
              )
            },
            {
              key: '4',
              disabled: viewComponents,
              label: (
                <span className={styles.tabLabel}>
                  <TcPerformanceIcon width={21} height={21} />
                  TC Performance
                </span>
              ),
              children: (
                <React.Suspense fallback={<Spin />}>
                  <Row gutter={[16, 16]}>
                    {/* <Col xs={24}>
                      <Card 
                        title="Organization Performance" 
                       // loading={loading}
                        extra={
                          <Tooltip title="Click on bars to filter by organization">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ height: chartDimensions.height+100 }}>
                          <BarChart 
                            data={processedData?.orgPerformance}
                            xField="org"
                            yFields={['COMPLETED', 'EXPIRED', 'CANCELED','UNABLE_TO_FULFILL', 'PENDING']}
                            width={chartDimensions.width * (windowSize.width < 992 ? 1 : 1.5)}
                            height={chartDimensions.height}
                            onBarClick={(data) => {
                              const orgId = healthOrganizations.find(o => o.name === data.org)?.id;
                              if (orgId) {
                                setSelectedOrg(orgId);
                                setActiveTab('2'); // Switch to detailed view
                              }
                            }}
                          />
                        </div>
                      </Card>
                    </Col> */}
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Account Activity
                            <Tooltip title="Activity by transport company">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        // loading={loading}
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setAccountActivityModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >
                        <div style={{ height: tcPerformanceContainerHeight }}>
                          <BarChart
                            data={processedData?.transportPerformance}
                            xField="company"
                            yFields={TcStatus}
                            width={chartDimensions.width}
                            height={tcPerformanceContainerHeight}
                            chartStyle="riderMetrics"
                            onBarClick={(data) => {
                              const tcId = transportCompanies.find(t => t.name === data.company)?.id;
                              if (tcId) {

                              }
                            }}
                          />
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Location Map
                            <Tooltip title="Location distribution by county based on pickup address">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        // loading={loading}
                        extra={
                          <div className={styles.inlineFlexGap12}>
                            <span className={styles.chartSectionTitle}>Rides by County</span>
                            <Tooltip title="Expand chart">
                              <button
                                type="button"
                                onClick={() => setLocationMapModalVisible(true)}
                                className={styles.iconButton}
                              >
                                <EnlargeIcon />
                              </button>
                            </Tooltip>
                          </div>
                        }
                      >
                        <div style={{ height: tcPerformanceContainerHeight }}>
                          <RidesByCountyChart
                            data={ridesData}
                            width={chartDimensions.width}
                            height={tcPerformanceCompactHeight}
                            mode="list"
                          />


                        </div>
                      </Card>
                    </Col>

                    {/* <Col xs={24}>
                      <Card
                        title="Performance"
                        // loading={loading}
                        extra={
                          <Tooltip title="Click on bars to filter by transport company">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ height: chartDimensions.height + 100 }}>
                          <BarChart
                            data={processedData?.transportPerformance}
                            xField="company"
                            yFields={['COMPLETED', 'FAILED']}
                            width={chartDimensions.width * (windowSize.width < 992 ? 1 : 1.5)}
                            height={chartDimensions.height}
                            onBarClick={(data) => {
                              const tcId = transportCompanies.find(t => t.name === data.company)?.id;
                              if (tcId) {
                                setSelectedTransport(tcId);
                                setActiveTab('2'); // Switch to detailed view
                              }
                            }}
                          />
                        </div>
                      </Card>
                    </Col> */}

                    {/* <Col xs={24}>
                      <Card
                        title="Driver Performance"
                      //  loading={loading}
                      >
                        <div style={{ height: chartDimensions.height }}>
                          <DriverPerformanceChart
                            data={processedData?.driverPerformance}
                            width={chartDimensions.width * (windowSize.width < 992 ? 1 : 1.5)}
                            height={chartDimensions.height}
                          />
                        </div>
                      </Card>
                    </Col> */}


                  </Row>
                </React.Suspense>
              )
            },
            {
              key: '6',
              disabled: viewComponents,
              label: (
                <span className={styles.tabLabel}>
                  <GeoInsightsIcon width={20} height={20} />
                  Geo Insights
                </span>
              ),
              children: (
                <React.Suspense fallback={<Spin />}>
                  {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }} justify="center">
                    <Col xs={24} lg={24}>



                    
                      <Card
                        title="Cost Center Distribution"
                        extra={
                          <Tooltip title="Ride distribution by cost center">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >



 
                        <div  style={{ height: chartDimensions.height+100 }}>
                          <div style={{ fontWeight: 500, marginBottom: 8 }}>
                            Total Cost Centers: {Object.keys(processedData?.costCenterCounts || {}).length}
                          </div>
                         
                          <StatusPieChart
                            data={Object.entries(processedData?.costCenterCounts || {}).map(([costCenter, count]) => ({
                              label: costCenter,
                              key: costCenter,
                              value: count,
                              color: stringToColor(costCenter)
                            }))}
                            width={chartDimensions.width+100}
                            height={chartDimensions.height+100}
                            onSegmentClick={(segment) => {
                              // Handle cost center selection if needed
                            }}
                          />
 
                          
                        </div>
                      </Card>
                    </Col>
                  </Row> */}
                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} lg={12}>

                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Geographic Insights for HO
                            <Tooltip title="Shows ride distribution by county based on pickup address">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        //   loading={loading}
                        extra={
                          <div className={styles.inlineFlexGap10}>
                            <span className={styles.chartSectionTitle}>Rides by County</span>
                            <Tooltip title="Expand chart">
                              <button
                                type="button"
                                onClick={() => setGeoInsightsModalVisible(true)}
                                className={styles.iconButton}
                              >
                                <EnlargeIcon />
                              </button>
                            </Tooltip>
                          </div>
                        }
                      >
                        <div style={{ height: geoInsightsSharedHeight }}>
                          <RidesByCountyChart
                            data={ridesData}
                            width={chartDimensions.width}
                            height={geoInsightsSharedHeight}
                          />
                        </div>
                      </Card>
                    </Col>
                  
                    {/* Distance Distribution by Cost Center */}
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Distance Distribution by Cost Center/Organization
                            <Tooltip title="Shows urban, suburban, and rural ride distribution for each cost center (uses organization name if cost center is not assigned)">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setCostCenterDistanceModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >
                        <div style={{ height: geoInsightsSharedHeight }}>
                          <BarChart
                            data={processedData.costCenterByDistance || []}
                            xField="costCenter"
                            yFields={[
                              { key: 'urban', label: 'Urban (≤ 50 miles)', color: '#6FA4E8' },
                              { key: 'subUrban', label: 'Suburban (51-75 miles)', color: '#B296E4' },
                              { key: 'rural', label: 'Rural (> 75 miles)', color: '#42567E' }
                            ]}
                            width={chartDimensions.width}
                            height={geoInsightsSharedHeight}
                            chartStyle="geoInsights"
                            onBarClick={(data) => {
                            }}
                          />
                        </div>
                      </Card>
                    </Col>
                  </Row>


                  {/* Cost Center by County Heat Matrix */}
                  {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24}>
                      <Card
                        title="Cost Center/Organization by County Distribution"
                        extra={
                          <Tooltip title="Shows which cost centers serve which counties (uses organization name if cost center is not assigned)">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ minHeight: 400 }}>
                          {processedData.costCenterCountyMatrix && processedData.costCenterCountyMatrix.length > 0 ? (
                            <Table
                              dataSource={(() => {
                                // Group by cost center
                                const grouped = {};
                                processedData.costCenterCountyMatrix.forEach(item => {
                                  if (!grouped[item.costCenter]) {
                                    grouped[item.costCenter] = {
                                      costCenter: item.costCenter,
                                      counties: {},
                                      total: 0
                                    };
                                  }
                                  grouped[item.costCenter].counties[item.county] = item.count;
                                  grouped[item.costCenter].total += item.count;
                                });

                                return Object.values(grouped).sort((a, b) => b.total - a.total);
                              })()}
                              columns={[
                                {
                                  title: 'Cost Center / Organization',
                                  dataIndex: 'costCenter',
                                  key: 'costCenter',
                                  fixed: 'left',
                                  width: 200,
                                  render: (text) => <strong>{text}</strong>
                                },
                                ...Array.from(
                                  new Set(processedData.costCenterCountyMatrix.map(item => item.county))
                                ).sort().map(county => ({
                                  title: county,
                                  dataIndex: ['counties', county],
                                  key: county,
                                  width: 120,
                                  align: 'center',
                                  render: (count) => count ? (
                                    <Badge
                                      count={count}
                                      style={{
                                        backgroundColor: count > 10 ? '#52c41a' : count > 5 ? '#1890ff' : '#faad14'
                                      }}
                                    />
                                  ) : '-'
                                })),
                                {
                                  title: 'Total Rides',
                                  dataIndex: 'total',
                                  key: 'total',
                                  fixed: 'right',
                                  width: 120,
                                  align: 'center',
                                  render: (total) => <strong>{total}</strong>
                                }
                              ]}
                              pagination={{ pageSize: 10 }}
                              scroll={{ x: 'max-content' }}
                              size="small"
                            />
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                              No data available
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row> */}

                  {/* Top Counties by Cost Center */}
                  {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24}>
                      <Card
                        title="Top 5 Counties per Cost Center/Organization"
                        extra={
                          <Tooltip title="Shows the top 5 counties served by each cost center (uses organization name if cost center is not assigned)">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ minHeight: 300 }}>
                          {processedData.costCenterCountyMatrix && processedData.costCenterCountyMatrix.length > 0 ? (
                            (() => {
                              // Group by cost center and get top 5 counties for each
                              const grouped = {};
                              processedData.costCenterCountyMatrix.forEach(item => {
                                if (!grouped[item.costCenter]) {
                                  grouped[item.costCenter] = [];
                                }
                                grouped[item.costCenter].push({ county: item.county, count: item.count });
                              });

                              return Object.entries(grouped).map(([costCenter, counties]) => {
                                const topCounties = counties
                                  .sort((a, b) => b.count - a.count)
                                  .slice(0, 5);

                                return (
                                  <div key={costCenter} style={{ marginBottom: 24 }}>
                                    <h4 style={{ marginBottom: 12, color: '#1890ff' }}>{costCenter}</h4>
                                    <Row gutter={[8, 8]}>
                                      {topCounties.map((item, index) => (
                                        <Col key={item.county} xs={24} sm={12} md={8} lg={4}>
                                          <Card size="small" style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: 12, color: '#999' }}>#{index + 1}</div>
                                            <div style={{ fontWeight: 'bold', margin: '8px 0' }}>{item.county}</div>
                                            <div style={{ fontSize: 20, color: '#1890ff' }}>{item.count}</div>
                                            <div style={{ fontSize: 12, color: '#999' }}>rides</div>
                                          </Card>
                                        </Col>
                                      ))}
                                    </Row>
                                  </div>
                                );
                              });
                            })()
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                              No data available
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row> */}

                  {/* Top Routes by Cost Center */}
                  {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24}>
                      <Card
                        title="Top Routes by Cost Center/Organization"
                        extra={
                          <Tooltip title="Most common pickup-to-dropoff county routes for each cost center (uses organization name if cost center is not assigned)">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ minHeight: 300 }}>
                          {processedData.costCenterTopRoutes && Object.keys(processedData.costCenterTopRoutes).length > 0 ? (
                            Object.entries(processedData.costCenterTopRoutes).map(([costCenter, routes]) => (
                              <div key={costCenter} style={{ marginBottom: 32 }}>
                                <h4 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #f0f0f0', paddingBottom: 8 }}>
                                  {costCenter}
                                </h4>
                                <Table
                                  dataSource={routes.map((route, idx) => ({ ...route, key: idx }))}
                                  columns={[
                                    {
                                      title: 'Rank',
                                      dataIndex: 'rank',
                                      key: 'rank',
                                      width: 80,
                                      align: 'center',
                                      render: (_, __, index) => (
                                        <Badge
                                          count={index + 1}
                                          style={{
                                            backgroundColor: index < 3 ? '#52c41a' : '#1890ff'
                                          }}
                                        />
                                      )
                                    },
                                    {
                                      title: 'Pickup County',
                                      dataIndex: 'pickupCounty',
                                      key: 'pickupCounty',
                                      render: (text) => <strong>{text}</strong>
                                    },
                                    {
                                      title: '',
                                      key: 'arrow',
                                      width: 50,
                                      align: 'center',
                                      render: () => <span style={{ fontSize: 18, color: '#1890ff' }}>→</span>
                                    },
                                    {
                                      title: 'Dropoff County',
                                      dataIndex: 'dropoffCounty',
                                      key: 'dropoffCounty',
                                      render: (text) => <strong>{text}</strong>
                                    },
                                    {
                                      title: 'Total Rides',
                                      dataIndex: 'count',
                                      key: 'count',
                                      align: 'center',
                                      sorter: (a, b) => b.count - a.count,
                                      render: (count) => (
                                        <span style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
                                          {count}
                                        </span>
                                      )
                                    },
                                    {
                                      title: 'Route Type',
                                      key: 'routeType',
                                      align: 'center',
                                      render: (_, record) => (
                                        <Badge
                                          status={record.pickupCounty === record.dropoffCounty ? 'success' : 'processing'}
                                          text={record.pickupCounty === record.dropoffCounty ? 'Same County' : 'Cross County'}
                                        />
                                      )
                                    }
                                  ]}
                                  pagination={false}
                                  size="small"
                                />
                              </div>
                            ))
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                              No route data available
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row> */}

                  {/* Cost Center Activity Timeline */}
                  {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24}>
                      <Card
                        title="Cost Center/Organization Activity Timeline (By Hour of Day)"
                        extra={
                          <Tooltip title="Shows when rides occur throughout the day for each cost center (uses organization name if cost center is not assigned)">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ minHeight: 400 }}>
                          {processedData.costCenterActivityTimeline && processedData.costCenterActivityTimeline.length > 0 ? (
                            processedData.costCenterActivityTimeline.map((costCenterData, idx) => {
                              const maxCount = Math.max(...costCenterData.hourly.map(h => h.count));

                              return (
                                <div key={idx} style={{ marginBottom: 40 }}>
                                  <h4 style={{ marginBottom: 16, color: '#1890ff' }}>
                                    {costCenterData.costCenter}
                                    <span style={{ fontSize: 14, color: '#999', marginLeft: 8 }}>
                                      (Total: {costCenterData.total} rides)
                                    </span>
                                  </h4>

                                   Hour-by-hour grid visualization 
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(24, 1fr)',
                                    gap: 4,
                                    marginBottom: 8
                                  }}>
                                    {costCenterData.hourly.map((hourData) => {
                                      const intensity = maxCount > 0 ? (hourData.count / maxCount) : 0;
                                      const backgroundColor = hourData.count === 0
                                        ? '#f5f5f5'
                                        : `rgba(24, 144, 255, ${0.2 + intensity * 0.8})`;

                                      return (
                                        <Tooltip
                                          key={hourData.hour}
                                          title={`${hourData.hour}:00 - ${hourData.count} rides`}
                                        >
                                          <div style={{
                                            backgroundColor,
                                            height: 60,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            border: '1px solid #d9d9d9',
                                            transition: 'all 0.3s'
                                          }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = 'scale(1.05)';
                                              e.currentTarget.style.zIndex = '10';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = 'scale(1)';
                                              e.currentTarget.style.zIndex = '1';
                                            }}
                                          >
                                            <div style={{ fontSize: 10, fontWeight: 'bold' }}>
                                              {hourData.hour}
                                            </div>
                                            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#1890ff' }}>
                                              {hourData.count}
                                            </div>
                                          </div>
                                        </Tooltip>
                                      );
                                    })}
                                  </div>

                                  Time period labels
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: 11,
                                    color: '#999',
                                    paddingTop: 4
                                  }}>
                                    <span>Midnight</span>
                                    <span>6 AM</span>
                                    <span>Noon</span>
                                    <span>6 PM</span>
                                    <span>Midnight</span>
                                  </div>

                                  Peak hours indicator 
                                  <div style={{ marginTop: 12, fontSize: 13 }}>
                                    <strong>Peak Hours: </strong>
                                    {(() => {
                                      const peakHours = costCenterData.hourly
                                        .filter(h => h.count > 0)
                                        .sort((a, b) => b.count - a.count)
                                        .slice(0, 3);

                                      return peakHours.length > 0
                                        ? peakHours.map((h, i) => (
                                          <Badge
                                            key={h.hour}
                                            count={`${h.hour}:00 (${h.count} rides)`}
                                            style={{
                                              backgroundColor: i === 0 ? '#52c41a' : i === 1 ? '#1890ff' : '#faad14',
                                              marginRight: 8
                                            }}
                                          />
                                        ))
                                        : <span style={{ color: '#999' }}>No data</span>;
                                    })()}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                              No activity timeline data available
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row> */}

                  {/* Cost Center Service Coverage Map */}
                  {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24}>
                      <Card
                        title="Cost Center/Organization Service Coverage Summary"
                        extra={
                          <Tooltip title="Geographic service coverage by cost center showing pickup and dropoff locations (uses organization name if cost center is not assigned)">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ minHeight: 400 }}>
                          {processedData.costCenterLocations && processedData.costCenterLocations.length > 0 ? (
                            processedData.costCenterLocations.map((costCenterData, idx) => {
                              // Group locations by county for summary
                              const pickupCounties = {};
                              const dropoffCounties = {};

                              costCenterData.pickupLocations.forEach(loc => {
                                const county = loc.county || 'Unknown';
                                pickupCounties[county] = (pickupCounties[county] || 0) + 1;
                              });

                              costCenterData.dropoffLocations.forEach(loc => {
                                const county = loc.county || 'Unknown';
                                dropoffCounties[county] = (dropoffCounties[county] || 0) + 1;
                              });

                              return (
                                <div key={idx} style={{ marginBottom: 32, borderBottom: '1px solid #f0f0f0', paddingBottom: 24 }}>
                                  <h4 style={{ marginBottom: 16, color: '#1890ff' }}>
                                    {costCenterData.costCenter}
                                  </h4>

                                  <Row gutter={[16, 16]}>
                                    <Col xs={24} md={8}>
                                      <Card size="small" style={{ backgroundColor: '#e6f7ff' }}>
                                        <Statistic
                                          title="Total Rides"
                                          value={costCenterData.rides.length}
                                          prefix={<CarOutlined />}
                                          styles={{ content: { color: '#1890ff' } }}
                                        />
                                      </Card>
                                    </Col>
                                    <Col xs={24} md={8}>
                                      <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                                        <Statistic
                                          title="Pickup Locations"
                                          value={costCenterData.pickupLocations.length}
                                          styles={{ content: { color: '#52c41a' } }}
                                        />
                                      </Card>
                                    </Col>
                                    <Col xs={24} md={8}>
                                      <Card size="small" style={{ backgroundColor: '#fff7e6' }}>
                                        <Statistic
                                          title="Dropoff Locations"
                                          value={costCenterData.dropoffLocations.length}
                                          styles={{ content: { color: '#faad14' } }}
                                        />
                                      </Card>
                                    </Col>
                                  </Row>

                                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                    <Col xs={24} md={12}>
                                      <h5 style={{ marginBottom: 8 }}>Pickup Counties Coverage</h5>
                                      <div className={styles.thinScrollbar}>
                                        {Object.entries(pickupCounties)
                                          .sort((a, b) => b[1] - a[1])
                                          .map(([county, count]) => (
                                            <div key={county} style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              padding: '4px 8px',
                                              backgroundColor: '#f5f5f5',
                                              marginBottom: 4,
                                              borderRadius: 4
                                            }}>
                                              <span>{county}</span>
                                              <Badge
                                                count={count}
                                                style={{ backgroundColor: '#52c41a' }}
                                              />
                                            </div>
                                          ))}
                                      </div>
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <h5 style={{ marginBottom: 8 }}>Dropoff Counties Coverage</h5>
                                      <div className={styles.thinScrollbar}>
                                        {Object.entries(dropoffCounties)
                                          .sort((a, b) => b[1] - a[1])
                                          .map(([county, count]) => (
                                            <div key={county} style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              padding: '4px 8px',
                                              backgroundColor: '#f5f5f5',
                                              marginBottom: 4,
                                              borderRadius: 4
                                            }}>
                                              <span>{county}</span>
                                              <Badge
                                                count={count}
                                                style={{ backgroundColor: '#faad14' }}
                                              />
                                            </div>
                                          ))}
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                              No location data available
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row> */}

                  {/* Cost Center Cancellation and Completion by Location */}
                  {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24}>
                      <Card
                        title="Cost Center/Organization Performance by Location (County)"
                        extra={
                          <Tooltip title="Shows completion and cancellation rates for each cost center by county (uses organization name if cost center is not assigned)">
                            <InfoIcon width={30} height={30} />
                          </Tooltip>
                        }
                      >
                        <div style={{ minHeight: 400 }}>
                          {processedData.costCenterLocationPerformance &&
                            Object.keys(processedData.costCenterLocationPerformance).length > 0 ? (
                            Object.entries(processedData.costCenterLocationPerformance).map(([costCenter, locations]) => (
                              <div key={costCenter} style={{ marginBottom: 40, borderBottom: '1px solid #f0f0f0', paddingBottom: 24 }}>
                                <h4 style={{ marginBottom: 16, color: '#1890ff', borderLeft: '4px solid #1890ff', paddingLeft: 12 }}>
                                  {costCenter}
                                </h4>

                                <Table
                                  dataSource={locations.map((loc, idx) => ({ ...loc, key: idx }))}
                                  columns={[
                                    {
                                      title: 'County',
                                      dataIndex: 'county',
                                      key: 'county',
                                      width: 180,
                                      render: (text) => <strong>{text}</strong>,
                                      sorter: (a, b) => a.county.localeCompare(b.county)
                                    },
                                    {
                                      title: 'Total Rides',
                                      dataIndex: 'total',
                                      key: 'total',
                                      align: 'center',
                                      width: 120,
                                      sorter: (a, b) => b.total - a.total,
                                      defaultSortOrder: 'descend',
                                      render: (count) => (
                                        <Badge
                                          count={count}
                                          style={{ backgroundColor: '#1890ff' }}
                                        />
                                      )
                                    },
                                    {
                                      title: 'Completed',
                                      dataIndex: 'completed',
                                      key: 'completed',
                                      align: 'center',
                                      width: 120,
                                      sorter: (a, b) => b.completed - a.completed,
                                      render: (count) => (
                                        <Badge
                                          count={count}
                                          style={{ backgroundColor: '#52c41a' }}
                                        />
                                      )
                                    },
                                    {
                                      title: 'Completion Rate',
                                      dataIndex: 'completionRate',
                                      key: 'completionRate',
                                      align: 'center',
                                      width: 150,
                                      sorter: (a, b) => parseFloat(b.completionRate) - parseFloat(a.completionRate),
                                      render: (rate, record) => (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                          <div style={{
                                            width: 100,
                                            height: 20,
                                            backgroundColor: '#f0f0f0',
                                            borderRadius: 10,
                                            overflow: 'hidden'
                                          }}>
                                            <div style={{
                                              width: `${rate}%`,
                                              height: '100%',
                                              backgroundColor: '#52c41a',
                                              transition: 'width 0.3s'
                                            }} />
                                          </div>
                                          <strong style={{ color: '#52c41a', minWidth: 45 }}>{rate}%</strong>
                                        </div>
                                      )
                                    },
                                    {
                                      title: 'Canceled',
                                      dataIndex: 'canceled',
                                      key: 'canceled',
                                      align: 'center',
                                      width: 110,
                                      sorter: (a, b) => b.canceled - a.canceled,
                                      render: (count) => (
                                        <Badge
                                          count={count}
                                          style={{ backgroundColor: '#ff4d4f' }}
                                        />
                                      )
                                    },
                                    {
                                      title: 'Expired',
                                      dataIndex: 'expired',
                                      key: 'expired',
                                      align: 'center',
                                      width: 100,
                                      sorter: (a, b) => b.expired - a.expired,
                                      render: (count) => (
                                        <Badge
                                          count={count}
                                          style={{ backgroundColor: '#faad14' }}
                                        />
                                      )
                                    },
                                    {
                                      title: 'Cancellation Rate',
                                      dataIndex: 'cancellationRate',
                                      key: 'cancellationRate',
                                      align: 'center',
                                      width: 150,
                                      sorter: (a, b) => parseFloat(b.cancellationRate) - parseFloat(a.cancellationRate),
                                      render: (rate, record) => (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                          <div style={{
                                            width: 100,
                                            height: 20,
                                            backgroundColor: '#f0f0f0',
                                            borderRadius: 10,
                                            overflow: 'hidden'
                                          }}>
                                            <div style={{
                                              width: `${rate}%`,
                                              height: '100%',
                                              backgroundColor: '#ff4d4f',
                                              transition: 'width 0.3s'
                                            }} />
                                          </div>
                                          <strong style={{ color: '#ff4d4f', minWidth: 45 }}>{rate}%</strong>
                                        </div>
                                      )
                                    }
                                  ]}
                                  pagination={false}
                                  size="small"
                                  summary={(pageData) => {
                                    const totalRides = pageData.reduce((sum, item) => sum + item.total, 0);
                                    const totalCompleted = pageData.reduce((sum, item) => sum + item.completed, 0);
                                    const totalCanceled = pageData.reduce((sum, item) => sum + item.canceled, 0);
                                    const totalExpired = pageData.reduce((sum, item) => sum + item.expired, 0);
                                    const avgCompletionRate = totalRides > 0 ? ((totalCompleted / totalRides) * 100).toFixed(1) : 0;
                                    const avgCancellationRate = totalRides > 0 ? (((totalCanceled + totalExpired) / totalRides) * 100).toFixed(1) : 0;

                                    return (
                                      <Table.Summary fixed>
                                        <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                          <Table.Summary.Cell index={0}>
                                            <strong>Total Summary</strong>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={1} align="center">
                                            <Badge count={totalRides} style={{ backgroundColor: '#1890ff' }} />
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={2} align="center">
                                            <Badge count={totalCompleted} style={{ backgroundColor: '#52c41a' }} />
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={3} align="center">
                                            <strong style={{ color: '#52c41a' }}>{avgCompletionRate}%</strong>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={4} align="center">
                                            <Badge count={totalCanceled} style={{ backgroundColor: '#ff4d4f' }} />
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={5} align="center">
                                            <Badge count={totalExpired} style={{ backgroundColor: '#faad14' }} />
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={6} align="center">
                                            <strong style={{ color: '#ff4d4f' }}>{avgCancellationRate}%</strong>
                                          </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                      </Table.Summary>
                                    );
                                  }}
                                />

                                 Visual Summary Cards 
                                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                  <Col xs={24} sm={12} md={8}>
                                    <Card size="small" style={{ backgroundColor: '#e6f7ff', textAlign: 'center' }}>
                                      <Statistic
                                        title="Best Performing County"
                                        value={locations.length > 0 ?
                                          locations.reduce((max, loc) =>
                                            parseFloat(loc.completionRate) > parseFloat(max.completionRate) ? loc : max
                                          ).county : 'N/A'
                                        }
                                        styles={{ content: { fontSize: 16, color: '#52c41a' } }}
                                        suffix={
                                          locations.length > 0 ?
                                            <span style={{ fontSize: 14, color: '#52c41a' }}>
                                              ({locations.reduce((max, loc) =>
                                                parseFloat(loc.completionRate) > parseFloat(max.completionRate) ? loc : max
                                              ).completionRate}% completion)
                                            </span> : null
                                        }
                                      />
                                    </Card>
                                  </Col>
                                  <Col xs={24} sm={12} md={8}>
                                    <Card size="small" style={{ backgroundColor: '#fff1f0', textAlign: 'center' }}>
                                      <Statistic
                                        title="Highest Cancellation County"
                                        value={locations.length > 0 ?
                                          locations.reduce((max, loc) =>
                                            parseFloat(loc.cancellationRate) > parseFloat(max.cancellationRate) ? loc : max
                                          ).county : 'N/A'
                                        }
                                        styles={{ content: { fontSize: 16, color: '#ff4d4f' } }}
                                        suffix={
                                          locations.length > 0 ?
                                            <span style={{ fontSize: 14, color: '#ff4d4f' }}>
                                              ({locations.reduce((max, loc) =>
                                                parseFloat(loc.cancellationRate) > parseFloat(max.cancellationRate) ? loc : max
                                              ).cancellationRate}% cancellation)
                                            </span> : null
                                        }
                                      />
                                    </Card>
                                  </Col>
                                  <Col xs={24} sm={12} md={8}>
                                    <Card size="small" style={{ backgroundColor: '#f6ffed', textAlign: 'center' }}>
                                      <Statistic
                                        title="Most Active County"
                                        value={locations.length > 0 ?
                                          locations.reduce((max, loc) =>
                                            loc.total > max.total ? loc : max
                                          ).county : 'N/A'
                                        }
                                        styles={{ content: { fontSize: 16, color: '#1890ff' } }}
                                        suffix={
                                          locations.length > 0 ?
                                            <span style={{ fontSize: 14, color: '#1890ff' }}>
                                              ({locations.reduce((max, loc) =>
                                                loc.total > max.total ? loc : max
                                              ).total} rides)
                                            </span> : null
                                        }
                                      />
                                    </Card>
                                  </Col>
                                </Row>
                              </div>
                            ))
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                              No performance data available
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row> */}

                </React.Suspense>
              )
            },
            {
              key: '5',
              label: (
                <span className={styles.tabLabel}>
                  <CancellationIcon width={17} height={17} />
                  Cancellations
                </span>
              ),
              children: (
                <React.Suspense fallback={<Spin />}>
                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>

                    <Col xs={24} lg={12}>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Card className={styles.insightsChartCard}
                            title={
                              <span className={styles.inlineFlexGap8}>
                                Cancellation Reasons – Riders
                                <Tooltip title="Cancellation reasons by riders">
                                  <span className={styles.inlineFlexPointer}>
                                    <InfoIcon width={18} height={18} />
                                  </span>
                                </Tooltip>
                              </span>
                            }
                            extra={
                              <Tooltip title="Expand chart">
                                <button
                                  type="button"
                                  onClick={() => setRiderCancellationModalVisible(true)}
                                  className={styles.iconButton}
                                >
                                  <EnlargeIcon />
                                </button>
                              </Tooltip>
                            }
                          >
                            <div style={{ height: cancellationRidersChartHeight }}>
                              {getCancellationReasonSeries('RIDER').length === 0 ? (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  height: '100%',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  fontSize: '16px'
                                }}>
                                  No records found
                                </div>
                              ) : (
                                <CancellationStackedReasonChart
                                  data={getCancellationReasonSeries('RIDER')}
                                  totalLabel="Total Rider Cancellations"
                                />
                              )}
                            </div>
                          </Card>
                        </Col>
                        <Col span={24}>
                          <Card className={styles.insightsChartCard}
                            title={
                              <span className={styles.inlineFlexGap8}>
                                Cancellation Reasons – Facility
                                <Tooltip title="Cancellation reasons by health organization team">
                                  <span className={styles.inlineFlexPointer}>
                                    <InfoIcon width={18} height={18} />
                                  </span>
                                </Tooltip>
                              </span>
                            }
                            extra={
                              <Tooltip title="Expand chart">
                                <button
                                  type="button"
                                  onClick={() => setHoCancellationModalVisible(true)}
                                  className={styles.iconButton}
                                >
                                  <EnlargeIcon />
                                </button>
                              </Tooltip>
                            }
                          >
                            <div style={{ height: cancellationHoChartHeight }}>
                              {getCancellationReasonSeries('HO_ADMIN').length === 0 ? (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  height: '100%',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  fontSize: '16px'
                                }}>
                                  No records found
                                </div>
                              ) : (
                                <CancellationSemiDonutChart
                                  data={getCancellationReasonSeries('HO_ADMIN')}
                                  centerNumber={getCancellationReasonSeries('HO_ADMIN').reduce((sum, item) => sum + item.value, 0)}
                                  centerLabel="Facility Cancellations"
                                />
                              )}
                            </div>
                          </Card>
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card className={styles.insightsChartCard}
                        title={
                          <span className={styles.inlineFlexGap8}>
                            Canceled vs. Rescheduled Rides
                            <Tooltip title="Shows monthly trends of canceled vs rescheduled rides">
                              <span className={styles.inlineFlexPointer}>
                                <InfoIcon width={18} height={18} />
                              </span>
                            </Tooltip>
                          </span>
                        }
                        extra={
                          <Tooltip title="Expand chart">
                            <button
                              type="button"
                              onClick={() => setCanceledVsRescheduledModalVisible(true)}
                              className={styles.iconButton}
                            >
                              <EnlargeIcon />
                            </button>
                          </Tooltip>
                        }
                      >
                        <div style={{ height: cancellationTrendExpandedHeight }}>
                          {/* <MonthlyTrendChart
                            data={processedData?.monthlyTrends}
                            xField="month"
                            yFields={rideReschduleCancellation}
                            width={chartDimensions.width}
                            height={cancellationTrendExpandedHeight}
                          /> */}

                          <BarChart
                            data={processedData?.monthlyTrends}
                            xField="month"
                            yFields={rideReschduleCancellation}
                            width={chartDimensions.width}
                            height={cancellationTrendExpandedHeight}
                            chartStyle="geoInsights"
                            onBarClick={(data) => {

                            }}
                          />

                        </div>
                      </Card>
                    </Col>
                    {(!hideTcDriverCancellations) && (
                      <>
                        <Col xs={24} lg={12}>
                          <Card className={styles.insightsChartCard}
                            title={
                              <span className={styles.inlineFlexGap8}>
                                Cancellation Reasons – TCs
                                <Tooltip title="Cancellation reasons by transport companies">
                                  <span className={styles.inlineFlexPointer}>
                                    <InfoIcon width={18} height={18} />
                                  </span>
                                </Tooltip>
                              </span>
                            }
                            extra={
                              <Tooltip title="Expand chart">
                                <button
                                  type="button"
                                  onClick={() => setTcCancellationModalVisible(true)}
                                  className={styles.iconButton}
                                >
                                  <EnlargeIcon />
                                </button>
                              </Tooltip>
                            }
                          >
                            <div style={{ height: cancellationTcChartHeight }}>

                              {getCancellationReasonSeries('TC_ADMIN').length === 0 ? (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  height: '100%',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  fontSize: '16px'
                                }}>
                                  No records found
                                </div>
                              ) : (
                                <CancellationSemiDonutChart
                                  data={getCancellationReasonSeries('TC_ADMIN')}
                                  centerNumber={getCancellationReasonSeries('TC_ADMIN').reduce((sum, item) => sum + item.value, 0)}
                                  centerLabel="TC Cancellations"
                                  wrapTcAccommodationLegend
                                />
                              )}

                            </div>
                          </Card>
                        </Col>


                        <Col xs={24} lg={12}>
                          <Card className={styles.insightsChartCard}
                            title={
                              <span className={styles.inlineFlexGap8}>
                                Cancellation Reasons – Drivers
                                <Tooltip title="Cancellation reasons by drivers">
                                  <span className={styles.inlineFlexPointer}>
                                    <InfoIcon width={18} height={18} />
                                  </span>
                                </Tooltip>
                              </span>
                            }
                            extra={
                              <Tooltip title="Expand chart">
                                <button
                                  type="button"
                                  onClick={() => setDriverCancellationModalVisible(true)}
                                  className={styles.iconButton}
                                >
                                  <EnlargeIcon />
                                </button>
                              </Tooltip>
                            }
                          >
                            <div style={{ height: cancellationDriverChartHeight }}>
                              {getCancellationReasonSeries('DRIVER').length === 0 ? (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  height: '100%',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  fontSize: '16px'
                                }}>
                                  No records found
                                </div>
                              ) : (
                                <CancellationSemiDonutChart
                                  data={getCancellationReasonSeries('DRIVER')}
                                  centerNumber={getCancellationReasonSeries('DRIVER').reduce((sum, item) => sum + item.value, 0)}
                                  centerLabel="Driver Cancellations"
                                />
                              )}
                            </div>
                          </Card>
                        </Col>
                      </>
                    )}

                  </Row>
                </React.Suspense>
              )
            },
            {
              key: '7',
              label: (
                <span className={styles.tabLabel}>
                  <ReportsIcon width={17} height={17} fill="#fff" fillPath="#E1F4D3" />
                  Reports
                </span>
              ),
              children: (
                <ReportsSection
                  organizations={healthOrganizations}
                  organizationSections={
                    (userRole === USER_ROLES.HO_ADMIN && (!healthOrganizations || healthOrganizations.length === 1)) ? []
                    : (userRole === USER_ROLES.HO_ADMIN && healthOrganizations.length > 1) ? enterpriseChildOrgSections
                    : userRole === USER_ROLES.ENTERPRISE_ADMIN ? enterpriseChildOrgSections
                    : superAdminOrgSections
                  }
                  transportCompanies={transportCompanies}
                  dateRange={dateRange}
                  exportReport={exportReport}
                  selHoOrgId={(userRole === USER_ROLES.HO_ADMIN && healthOrganizations.length > 1) ? null : userInfo?.organizationId }
                  userRole={userRole}
                // loading={loading}
                />
              )
            }
          ].filter(item => !item.disabled)}
        />

        {/* Ride Status Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Ride Status
              <Tooltip title="Ride status distribution across all rides">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={rideStatusModalVisible}
          onCancel={() => setRideStatusModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <BarChart
                data={Object.entries(processedData?.statusCounts || {}).map(([status, count]) => ({
                  status: statusLabel[status] || status,
                  count,
                }))}
                xField="status"
                yFields={[{ key: 'count', label: 'Rides' }]}
                colors={Object.entries(processedData?.statusCounts || {}).map(([status]) => statusColors[status] || '#8c8c8c')}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* Rider cancellations expanded */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Cancellation Reasons – Riders
              <Tooltip title="Cancellation reasons by riders">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={riderCancellationModalVisible}
          onCancel={() => setRiderCancellationModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.modalCancellationReasonExpanded}>
              {getCancellationReasonSeries('RIDER').length === 0 ? (
                <div className={styles.modalCancellationReasonExpandedEmpty}>No records found</div>
              ) : (
                <CancellationStackedReasonChart
                  data={getCancellationReasonSeries('RIDER')}
                  totalLabel="Total Rider Cancellations"
                  expanded
                />
              )}
            </div>
          </React.Suspense>
        </Modal>

        {/* Canceled vs. Rescheduled expanded */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Canceled vs. Rescheduled Rides
              <Tooltip title="Shows monthly trends of canceled vs rescheduled rides">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={canceledVsRescheduledModalVisible}
          onCancel={() => setCanceledVsRescheduledModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <BarChart
                data={processedData?.monthlyTrends}
                xField="month"
                yFields={rideReschduleCancellation}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
                chartStyle="geoInsights"
                onBarClick={(data) => {

                }}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* By Facility cancellations expanded */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Cancellation Reasons – Facility
              <Tooltip title="Cancellation reasons by health organization team">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={hoCancellationModalVisible}
          onCancel={() => setHoCancellationModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.modalCancellationReasonExpanded}>
              {getCancellationReasonSeries('HO_ADMIN').length === 0 ? (
                <div className={styles.modalCancellationReasonExpandedEmpty}>No records found</div>
              ) : (
                <CancellationSemiDonutChart
                  data={getCancellationReasonSeries('HO_ADMIN')}
                  centerNumber={getCancellationReasonSeries('HO_ADMIN').reduce((sum, item) => sum + item.value, 0)}
                  centerLabel="Facility Cancellations"
                  expanded
                />
              )}
            </div>
          </React.Suspense>
        </Modal>

        {/* Driver cancellations expanded */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Cancellation Reasons – Drivers
              <Tooltip title="Cancellation reasons by drivers">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={driverCancellationModalVisible}
          onCancel={() => setDriverCancellationModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.modalCancellationReasonExpanded}>
              {getCancellationReasonSeries('DRIVER').length === 0 ? (
                <div className={styles.modalCancellationReasonExpandedEmpty}>No records found</div>
              ) : (
                <CancellationSemiDonutChart
                  data={getCancellationReasonSeries('DRIVER')}
                  centerNumber={getCancellationReasonSeries('DRIVER').reduce((sum, item) => sum + item.value, 0)}
                  centerLabel="Driver Cancellations"
                  expanded
                />
              )}
            </div>
          </React.Suspense>
        </Modal>

        {/* TC cancellations expanded */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Cancellation Reasons – TCs
              <Tooltip title="Cancellation reasons by transport companies">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={tcCancellationModalVisible}
          onCancel={() => setTcCancellationModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.modalCancellationReasonExpanded}>
              {getCancellationReasonSeries('TC_ADMIN').length === 0 ? (
                <div className={styles.modalCancellationReasonExpandedEmpty}>No records found</div>
              ) : (
                <CancellationSemiDonutChart
                  data={getCancellationReasonSeries('TC_ADMIN')}
                  centerNumber={getCancellationReasonSeries('TC_ADMIN').reduce((sum, item) => sum + item.value, 0)}
                  centerLabel="TC Cancellations"
                  wrapTcAccommodationLegend
                  expanded
                />
              )}
            </div>
          </React.Suspense>
        </Modal>

        {/* Utilization Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Utilization
              <Tooltip title="Utilization trends over time">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={utilizationModalVisible}
          onCancel={() => setUtilizationModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <MonthlyTrendChart
                data={processedData?.monthlyTrends}
                xField="month"
                yFields={rideStatuses}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* Vehicle Types Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Vehicle Types
              <Tooltip title="Vehicle types distribution across rides">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={vehicleTypeModalVisible}
          onCancel={() => setVehicleTypeModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500} style={{ width: '100%', minWidth: 0 }}>
              <VehicleTypeChart
                data={Object.entries(processedData?.vehicleTypeCounts || {}).map(([status, count]) => ({
                  label: VEHICLE_TYPES_LABEL[status] || status,
                  key: status,
                  value: count,
                  color: vehicleTypesColors[status.trim()] || '#d9d9d9'
                }))}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
                onSegmentClick={() => { }}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* Rider Metrics Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Rider Metrics
              <Tooltip title={
                <>
                  <div><strong>Mobility Indicators</strong></div>
                  <div>- No shows &gt; 5 within 90 days</div>
                  <div>- Cancellations &gt; 5 within 90 days</div>
                  <div>- Rider Age &gt; 70</div>
                  <div>- Rider Weight &gt; 300 lbs</div>
                </>
              }>
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={riderMetricsModalVisible}
          onCancel={() => setRiderMetricsModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <BarChart
                data={processedData?.monthlyPatientTrends}
                xField="month"
                yFields={[
                  { key: 'allPatients', label: 'All Patients', color: '#379B63' },
                  { key: 'specialAccommodations', label: 'Mobility Indicators', color: '#A1F4D8' },
                ]}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
                chartStyle="riderMetrics"
                onBarClick={() => { }}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* Risk Analysis Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Risk Analysis
              <Tooltip title="All patients' vs Mobility Indicators vs Frequent Riders">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={riskAnalysisModalVisible}
          onCancel={() => setRiskAnalysisModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <BarChart
                data={processedData?.monthlyPatientTrends}
                xField="month"
                yFields={[
                  { key: 'riderNoShow', label: 'Rider No Show', color: '#3E5A87' },
                  { key: 'specialAccommodations', label: 'Mobility Indicators', color: '#2E84FD' },
                  { key: 'frequentScheduled', label: 'Frequent Riders', color: '#9864E7' },
                ]}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
                chartStyle="riderMetrics"
                onBarClick={() => { }}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* User Activity Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              User Activity (Health Partners)
              <Tooltip title="Total HO team members who have scheduled rides">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={userActivityModalVisible}
          onCancel={() => setUserActivityModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <div className={styles.userActivityModalTop}>
            <span className={styles.userActivityTotal}>Total: {totalUserActivity}</span>
          </div>
          <div className={styles.chartHeight500}>
            {renderUserActivityBars(userActivityRows, 500)}
          </div>
        </Modal>

        {/* Account Activity Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Account Activity
              <Tooltip title="Activity by transport company">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={accountActivityModalVisible}
          onCancel={() => setAccountActivityModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <BarChart
                data={processedData?.transportPerformance}
                xField="company"
                yFields={TcStatus}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
                chartStyle="riderMetrics"
                onBarClick={() => {}}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* Location Map Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Location Map
              <Tooltip title="Location distribution by county based on pickup address">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={locationMapModalVisible}
          onCancel={() => setLocationMapModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <RidesByCountyChart
                data={ridesData}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
                mode="list"
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* Geographic Insights Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Geographic Insights for HO
              <Tooltip title="Shows ride distribution by county based on pickup address">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={geoInsightsModalVisible}
          onCancel={() => setGeoInsightsModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <RidesByCountyChart
                data={ridesData}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
              />
            </div>
          </React.Suspense>
        </Modal>

        {/* Cost Center Distance Expanded Modal */}
        <Modal
          title={
            <span className={styles.inlineFlexGap8}>
              Distance Distribution by Cost Center/Organization
              <Tooltip title="Shows urban, suburban, and rural ride distribution for each cost center (uses organization name if cost center is not assigned)">
                <span className={styles.inlineFlexPointer}>
                  <InfoIcon width={18} height={18} />
                </span>
              </Tooltip>
            </span>
          }
          open={costCenterDistanceModalVisible}
          onCancel={() => setCostCenterDistanceModalVisible(false)}
          footer={null}
          width="80vw"
          centered
          destroyOnHidden
        >
          <React.Suspense fallback={<Spin />}>
            <div className={styles.chartHeight500}>
              <BarChart
                data={processedData.costCenterByDistance || []}
                xField="costCenter"
                yFields={[
                  { key: 'urban', label: 'Urban (≤ 50 miles)', color: '#6FA4E8' },
                  { key: 'subUrban', label: 'Suburban (51-75 miles)', color: '#B296E4' },
                  { key: 'rural', label: 'Rural (> 75 miles)', color: '#42567E' }
                ]}
                width={Math.round(window.innerWidth * 0.8) - 48}
                height={500}
                chartStyle="geoInsights"
                onBarClick={() => {}}
              />
            </div>
          </React.Suspense>
        </Modal>

        <InsightsRideDetailsModal
          open={rideModalVisible}
          ride={selectedRide}
          onClose={() => setRideModalVisible(false)}
          healthOrganizations={healthOrganizations}
          transportCompanies={transportCompanies}
          drivers={drivers}
          hideTransportCompanyAndDriver={hideTransportCompanyAndDriver}
        />
      </Content>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  healthOrganizations: state.healthOrganizations.healthOrganizations,
  transportCompanies: state.transportOrganizations.transportOrganizations,
  drivers: state.transportOrganizations.drivers,
  //loading: state.rides.loading,
  ridesData: state.rides.reportingRides,
  summary: state.rides.reportingSummary,

});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchAllTransportOrganizations,
      fetchTCDrivers,
      fetchAllHealthOrganizations,
      resetDrivers,
      getReportingRides,
      exportReport,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(MH_Reporting_Dashboard);